// Service Plans Data - Detailed pricing and package information for spiritual services

export interface ServicePlan {
  id: string;
  name: string;
  price: string;
  originalPrice?: string;
  duration: string;
  features: string[];
  popular?: boolean;
  priests?: number;
  materials?: string[];
  includes?: string[];
  ritualSteps?: string[];
  bestFor?: string[];
  languages?: string[];
  specialFeatures?: string[];
  minAdvanceBooking?: string;
  cancellationPolicy?: string;
  whatThisFeelsLike?: string[];
  // Plan-level differentiators shown as pills on the card
  jaapCount?: string;        // e.g. "11,000 Jaap" — most prominent differentiator
  highlightTags?: string[];  // tags shown when home delivery is selected
  siteHighlightTags?: string[]; // tags shown when Antaryatra site toggle is ON (falls back to highlightTags if not set)
  hidden?: boolean;          // set true to hide this plan from the booking UI entirely
  planTag?: string;          // badge shown on the plan card, e.g. "Most Popular", "Best Value", "Recommended", "New"
}

export const servicePlans: Record<string, ServicePlan[]> = {
  'maha-mrityunjaya-pujan': [
    {
      id: 'basic',
      name: 'Basic Pujan',
      price: '₹15,000',
      originalPrice: '₹21,500',
      duration: '6 Hours',
      features: [
        'Traditional mantras',
        'Basic puja materials',
        'Single priest',
        'Simple ceremony'
      ],
      priests: 1,
      // materials: [
      //   'All Puja Samagri',
      //   'Havan Kund',
      //   'Flowers',
      //   'Prashadam/Fruits'
      // ],
      includes: [
        'Maha Mrityunjaya mantra (1,008 times) jaap',
        'Essential pooja samagri & havan setup',
        'Standard sankalp (health / protection / recovery)',
        'Basic Flowers and fruits/prashadam required for puja',
        'Pandit Ji Dakshina & Travel Charges'
      ],
      ritualSteps: [
        'Ganesh Poojan',
        'Sankalp',
        'Maha Mrityunjaya mantra (1008 times)',
        'Havan and Aarti'
      ],
      bestFor: ['General health and peace', 'Preventive health prayers', 'Mental or emotional stress', 'Personal sankalp or individual intent'],
      // // languages: ['Hindi', 'Sanskrit'],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
      whatThisFeelsLike: [
        'A sincere, focused healing ritual performed with discipline and correctness.'
      ],
      jaapCount: '1,008 Maha Mrityunjaya Jaap',
      highlightTags: ['Single sankalp', 'All pooja samagri included', 'Travel(to & from Varanasi), stay, dakhsina for Kaashi Acharya(s) ji included', 'Havan included'],
      siteHighlightTags: ['Single sankalp', 'All pooja samagri included', 'Kashi Acharya Dakshina & expenses included', 'Havan included', 'Performed at our Antaryatra site in Kashi for amplified benefits', 'Prashadam delivered to your home anywhere in World', 'Prasadam delivery times may vary based on location'],
      
    },
    {
      id: 'standard',
      name: 'Standard Pujan',
      price: '₹40,000',
      originalPrice: '₹45,000',
      duration: '2 Days',
      features: [
        'Complete mantras',
        'Premium puja materials',
        'Experienced priest',
        'Detailed ceremony',
        'Prasadam included'
      ],
      popular: true,
      priests: 3,
      // materials: [
      //   'All Puja Samagri',
      //   'Havan Kund',
      //   'Flowers',
      //   'Prashadam/Fruits'
      // ],
      includes: [
        'Maha Mrityunjaya mantra (11,000 times) jaap',
        'Senior Acharya supervision',
        'Essential pooja samagri & havan setup',
        'Expanded sankalp (individual + family intent)',
        'Standard Flowers and fruits/prashadam required for puja',
        'Pandit Ji Dakshina & Travel Charges',
        'Pre / Post Pooja Guidance'
      ],
      ritualSteps: [
        'Ganesh Poojan',
        'Sankalp',
        'Maha Mrityunjaya mantra (11000 times)',
        'Havan and Aarti'
      ],
      bestFor: ['Ongoing health challenges', 'Recovery phases', 'Family-level protection and reassurance'],
      // // languages: ['Sanskrit', 'Hindi', 'English'],
      // specialFeatures: [
      //   'Detailed ritual explanation',
      //   'Family participation encouraged',
      //   'Digital documentation'
      // ],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
      whatThisFeelsLike: [
        'A deeper, more immersive healing process with stronger collective invocation.'
      ],
      jaapCount: '11,000 Maha MrityunjayaJaap',
      highlightTags: ['Family sankalp', ' 1 Senior Kashi Acharya', 'Pre Consultation with Senior Kashi Acharya', 'All pooja samagri included', 'Havan included', 'Kashi Acharya Dakshina/Travel included'],
      siteHighlightTags: ['Online/ In person Family sankalp', 'All pooja samagri included', 'Kashi Acharya Dakshina & expenses included', 'Havan included', 'Performed at our Antaryatra site in Kashi for amplified benefits', 'Prashadam delivered to your home anywhere in World', 'Prasadam delivery times may vary based on location'],
      
    },
    {
      id: 'premium',
      name: 'Premium Pujan',
      price: '₹55,000',
      originalPrice: '₹60,000',
      duration: '3-4 Days(Higher Jaap Count preferred at our Kashi Site)',
      features: [
        'Extended ceremony',
        'Luxury puja setup',
        'Master priest',
        'Complete documentation',
        'Family feast'
      ],
      priests: 5,
      // materials: [
      //   'Premium sacred items',
      //   'Silver and brass vessels',
      //   'Exotic flowers',
      //   'Pure ghee and oils',
      //   'Traditional silk cloth',
      //   'Sacred herbs and spices'
      // ],
      includes: [
        'Maha Mrityunjaya mantra (51,000 times) jaap',
        'Senior Kashi-trained Acharya Ji leading the ritual',
        'Essential pooja samagri & havan setup',
        'Detailed, purpose-specific sankalp',
        'Premium Flowers and fruits/prashadam required for puja',
        'Pandit Ji Dakshina & Travel Charges',
        'Comprehensive pre- and post-pooja guidance'
      ],
      ritualSteps: [
        'Ganesh Poojan',
        'Detailed sankalp',
        'Maha Mrityunjaya mantra (51000 times)',
        'Havan, Aarti and Shanti path'
      ],
      bestFor: ['Major life events', 'Serious or long-standing health concerns', 'Critical life phases', 'Deep spiritual grounding and protection'],
      // // languages: ['Sanskrit', 'Hindi', 'English'],
      specialFeatures: [
        'Master priest with 15+ years experience',
        'Special Shiv ji sringar'
      ],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
      jaapCount: '51,000 Maha MrityunjayaJaap',
      highlightTags: ['Detailed Sankalp (purpose-specific)', '2 Senior Kashi Acharyas', 'Pre & Post Consultation with Senior Kashi Acharya', 'All pooja samagri included', 'Multiple Pandit Ji for continuous Jaap', 'Shanti Paath', 'Havan included', 'Kashi Acharya Dakshina/Travel included', 'Abhimantrit Rudraksha for deveotee'],
      siteHighlightTags: ['Detailed Sankalp (purpose-specific)', 'All pooja samagri included', 'Kashi Acharya Dakshina & expenses included', 'Havan included', 'Performed at our Antaryatra site in Kashi for amplified benefits', 'Prashadam & Rudrakasha Mala delivered to your home anywhere in World', 'Prasadam delivery times may vary based on location',],
      whatThisFeelsLike: [
        'An anushthan-style invocation with intensity, discipline, and sustained spiritual focus.'
      ]
    }
  ],
  'rudrabhishekh': [
    {
      id: 'basic',
      name: 'Basic Rudrabhishekh',
      price: '₹25,000',
      originalPrice: '₹12,000',
      duration: '4 hours',
      features: [
        'Traditional Rudra mantras',
        'Basic abhishekh materials',
        'Single priest',
        'Simple ceremony'
      ],
      priests: 1,
      // materials: [
      //   'Sacred water',
      //   'Milk and honey',
      //   'Bilva leaves',
      //   'Basic flowers',
      //   'Incense sticks'
      // ],
      includes: [
        'Single-cycle Rudrabhishek and Rudra Ashtadhyayi Paath (standard recitation)',
        'Standard sankalp',
        'Essential pooja samagri & abhishek dravyas', 
        'Flowers and fruits', 
        'Dakshina and pandit travel charges'
      ],
      ritualSteps: [
        'Ganesh Smaran',
        'Sankalp',
        'Rudrabhishek (Milk) and Rudra Ashtadhyayi Paath',
        'Shringar',
        'Aarti'
      ],
      bestFor: ['Regular Shiva devotion', 
        'Personal peace and clarity', 
        'Beginning a new phase of life'],
      // // languages: ['Hindi', 'Sanskrit'],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
      jaapCount: 'Single Rudra Ashtadhyayi Paath',
      highlightTags: ['Milk & Ganga Jal abhishek', 'Shodashopchaar','Personal sankalp', 'All Samagri included', 'Senior Kashi Acharya', 'Kashi Acharya Dakshina/Travel included', 'Sringar included'],
      siteHighlightTags: ['Milk & Ganga Jal abhishek', 'Shodashopchaar','Personal sankalp', 'All Samagri included', 'Senior Kashi Acharya', 'Kashi Acharya Dakshina & expenses included', 'Sringar included', 'Performed at our Antaryatra site in Kashi for amplified benefits','Prashadam delivered to your home anywhere in World', 'Prasadam delivery times may vary based on location'],
      whatThisFeelsLike: [
        'A calm, devotional Rudrabhishek performed with purity and focus, ideal for regular Shiva upasana'
      ]
    },
    {
      id: 'standard',
      name: 'Standard Rudrabhishekh',
      price: '₹25,000',
      originalPrice: '₹20,000',
      duration: '5 hours',
      popular: true,
      features: [
        'Complete Rudra recitation',
        'Premium abhishekh materials',
        'Experienced priest',
        'Decorated altar setup'
      ],
      priests: 5,
      // materials: [
      //   'Sacred Ganga water',
      //   'Pure cow milk',
      //   'Organic honey',
      //   'Fresh bilva leaves',
      //   'Premium flowers',
      //   'Sandalwood paste',
      //   'Sacred ash (vibhuti)'
      // ],
      includes: [
        'Collective Rudrabhishek (Rudra Ashtadhyayi Paath)',
        'Senior Acharya supervision',
        'Expanded sankalp (individual + family intent)',
        'Pre- and post-pooja guidance', 
        'Essential pooja samagri & abhishek dravyas', 
        'Standard Flowers and fruits/Prashadam required for puja', 
        'Dakshina and pandit travel charges'
      ],
      ritualSteps: [
        'Ganesh Poojan',
        'Detailed sankalp',
        'Rudrabhishek (Rudra Ashtadhyayi Paath)',
        'Shringar',
        'Detailed abhishekh with 5 sacred liquids',
        'Aarti and blessings'
      ],
      bestFor: ['Family harmony and positivity', 'Emotional balance', 'Periodic spiritual strengthening'],
      // // languages: ['Sanskrit', 'Hindi', 'English'],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
      jaapCount: 'Pancha Rudra Ashtadhyayi Paath',
      highlightTags: ['Milk & Ganga Jal abhishek', 'Shodashopchaar', 'Family sankalp', 'Pre Consultation with Senior Acharya', '2 Senior Kashi Acharyas', 'All Kashi Acharya Dakshina/Travel included', 'Special sringar', 'All Samagri included'],
      siteHighlightTags: ['Milk & Ganga Jal abhishek', 'Shodashopchaar', 'Family sankalp', 'Pre Consultation with Senior Acharya', '2 Senior Kashi Acharyas', 'All Kashi Acharya Dakshina & expense included', 'Special sringar', 'All Samagri included', 'Performed at our Antaryatra site in Kashi for amplified benefits','Prashadam delivered to your home anywhere in World', 'Prasadam delivery times may vary based on location'],
    },
    {
      id: 'premium',
      name: 'Premium Rudrabhishekh',
      price: '₹151,000',
      originalPrice: '₹151,000',
      duration: '7 hours',
      features: [
        'Extensive Rudra recitation',
        'Luxury abhishekh setup',
        'Senior priest with 15+ years experience',
        'Complete ceremony documentation'
      ],
      priests: 11,
      // materials: [
      //   'Sacred Ganga water from Haridwar',
      //   'Pure A2 cow milk',
      //   'Wild forest honey',
      //   'Hand-picked bilva leaves',
      //   'Premium lotus flowers',
      //   'Pure sandalwood paste',
      //   'Sacred vibhuti from Kashi',
      //   'Rudraksha garlands'
      // ],
      includes: [
        'Ekadasha Rudrabhishek (Rudra Ashtadhyayi Paath)',
        'Senior Kashi-trained Acharya leading the ritual',
        'Detailed, purpose-specific sankalp',
        'Comprehensive pre- and post-pooja guidance',
        'Essential pooja samagri & abhishek dravyas',
        'Premium Flowers and fruits/Prashadam required for puja', 
        'Dakshina and pandit travel charges'
      ],
      ritualSteps: [
        'Ganesh Smaran',
        'Detailed sankalp',
        'Ekadasha Rudrabhishek (Collective Rudra Ashtadhyayi Paath)',
        'Divya Shringar',
        'Aarti and Shanti Paath',
      
      ],
      bestFor: ['Major life transitions', 'Long-standing obstacles', 'Deep Shiva sadhana and spiritual upliftment'],
      // // languages: ['Sanskrit', 'Hindi', 'English'],
      specialFeatures: [
        'Senior priest with 15+ years experience',
        'Complimentary rudraksha'
      ],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
      jaapCount: 'Ekadasha Rudra Ashtadhyayi Paath',
      highlightTags: ['3 Senior Kashi Acharya', 'Milk & Ganga Jal abhishek', 'Shodashopchaar', 'Rudrashtakam Paath', 'Bilvashtak paath','Divya Shringar',
         'Complimentary Rudraksha', 'All Kashi Acharya Dakshina/Travel included', 'Pre Consultation with Senior Acharya', 'All Samagri included'],
      siteHighlightTags: ['3 Senior Kashi Acharya', 'Milk & Ganga Jal abhishek', 'Shodashopchaar', 'Rudrashtakam Paath', 'Bilvashtak paath','Divya Shringar',
         'Complimentary Rudraksha', 'All Kashi Acharya Dakshina & expenses included', 'Pre Consultation with Senior Acharya', 'All Samagri included', 'Performed at our Antaryatra site in Kashi for amplified benefits','Prashadam & Rudrakasha Mala delivered to your home anywhere in World', 'Prasadam delivery times may vary based on location'],
    }
  ],
  'maha-rudrai-anushthaan': [
    {
      id: 'basic',
      name: 'Basic Rudrai Anushthaan',
      price: '₹25,000',
      originalPrice: '₹30,000',
      duration: '4 hours',
      features: [
        'Basic Rudra path recitation',
        'Traditional materials',
        'Qualified priests',
        'Sacred fire ceremony'
      ],
      priests: 2,
      // materials: [
      //   'Sacred threads',
      //   'Ghee for havan',
      //   'Sandalwood paste',
      //   'Fresh flowers',
      //   'Rice and sesame seeds',
      //   'Sacred herbs'
      // ],
      includes: [
      
        'Fixed-duration Rudraaii Paath',
        'Standard sankalp',
        'Essential pooja samagri & havan setup',
        'Flowers and fruits', 
        'Dakshina and pandit travel charges'
      ],
      ritualSteps: [
        'Ganesh Smaran',
        'Sankalp',
        'Daily Rudraaii Paath (fixed count)',
        'Aarti and blessings'
      ],
      bestFor: ['Entry into anushthan-level worship', 'Focused personal sadhana', 'Structured spiritual discipline'],
      // // languages: ['Hindi', 'Sanskrit'],
      hidden: true, // hide basic plan as it doesn't align with our current offerings and may cause confusion
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.'
    },
    {
      id: 'standard',
      name: 'Standard Rudrai Anushthaan',
      price: '₹35,000',
      originalPrice: '₹40,000',
      duration: '3 Days',
      features: [
        'Complete Rudra path recitation',
        'Premium materials',
        'Experienced priests',
        'Full havan ceremony',
        'Video recording included'
      ],
      popular: true,
      priests: 11,
      // materials: [
      //   'Premium sacred threads',
      //   'Pure ghee and oils',
      //   'Sandalwood and camphor',
      //   'Lotus flowers',
      //   'Sacred fire wood',
      //   'Traditional offerings'
      // ],
      includes: [
        'Multi-day Rudraaii Paath with defined daily discipline',
        'Senior Acharya supervision',
        'Expanded sankalp (individual + family intent)',
        'Pre- and post-anushthan guidance',
        'Essential pooja samagri',
        'Flowers and fruits',
        'Dakshina and pandit travel charges'
      ],
      ritualSteps: [
        'Ganesh Poojan',
        'Detailed sankalp',
        'Daily extended Rudraaii Paath',
        'Aarti and blessings'
      ],
      bestFor: ['Severe or deeply rooted obstacles', 'Critical karmic or life transitions', 'Serious Shiva sadhana and spiritual elevation', 'Life transitions'],
      // // languages: ['Sanskrit', 'Hindi', 'English'],
      specialFeatures: [
        'Complete Rudraaii Paath recitation',
        'Group chanting by priests'
      ],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
      jaapCount: '51 Rudra Ashtadhyayi Paath',
      highlightTags: ['3 Senior Kashi Acharya', 'Milk & Ganga Jal abhishek', 'Shodashopchaar', 'Rudrashtakam Paath', 'Bilvashtak paath','Divya Shringar',
         'Complimentary Rudraksha', 'All Kashi Acharya Dakshina/Travel included', 'Pre Consultation with Senior Acharya', 'All Samagri included'],
      siteHighlightTags: ['3 Senior Kashi Acharya', 'Milk & Ganga Jal abhishek', 'Shodashopchaar', 'Rudrashtakam Paath', 'Bilvashtak paath','Divya Shringar',
         'Complimentary Rudraksha', 'All Kashi Acharya Dakshina & expenses included', 'Pre Consultation with Senior Acharya', 'All Samagri included', 'Performed at our Antaryatra site in Kashi for amplified benefits','Prashadam & Rudrakasha Mala delivered to your home anywhere in World', 'Prasadam delivery times may vary based on location'],
   
    },
    {
      id: 'premium',
      name: 'Premium Rudrai Anushthaan',
      price: '₹50,000',
      originalPrice: '₹65,000',
      duration: '8 hours',
      features: [
        'Extensive Rudra path recitation',
        'Luxury ceremonial setup',
        'Master priests',
        'Grand havan ceremony',
        'Complete documentation',
        'Family feast included'
      ],
      priests: 9,
      // materials: [
      //   'Premium sacred items',
      //   'Pure cow ghee',
      //   'Rare sandalwood',
      //   'Exotic flowers and garlands',
      //   'Sacred fire arrangements',
      //   'Traditional brass vessels',
      //   'Silk cloth offerings'
      // ],
      includes: [
        'Vedic pandits led by a Senior Kashi-trained Acharya',
        'Extended multi-day Rudraaii Paath with high mantra counts',
        'Detailed, purpose-specific sankalp',
        'Comprehensive pre- and post-anushthan guidance',
        'Essential pooja samagri',
        'Flowers and fruits/Prashadam',
        'Dakshina and pandit travel charges'
      ],
      ritualSteps: [
        'Ganesh Smaran',
        'Detailed sankalp',
        'Multi-day collective Rudraaii Paath',
        'Completion Aarti and Shanti Paath',
      ],
      bestFor: ['Major life events', 'Spiritual advancement', 'Business prosperity', 'Family blessings'],
      // // languages: ['Sanskrit', 'Hindi', 'English'],
      specialFeatures: [
        'Extended Rudra recitation ',
        'Grand sacred fire ceremony',
        'Senior Kashi-trained Acharya guidance'
      ],
      hidden: true, // hide premium plan as it doesn't align with our current offerings and may cause confusion
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.'
    }
  ],
  'durga-saptshati-path': [
    {
      id: 'basic',
      name: 'Basic Durga Saptshati',
      price: '₹15,000',
      originalPrice: '₹18,000',
      duration: '5 hours',
      features: [
        'Essential Saptshati recitation',
        'Basic ceremonial setup',
        'Qualified priests',
        'Traditional mantras'
      ],
      priests: 1,
      // materials: [
      //   'Sacred Saptshati text',
      //   'Red flowers and garlands',
      //   'Kumkum and turmeric',
      //   'Basic incense sticks',
      //   'Simple oil lamps',
      //   'Coconut offerings'
      // ],
      includes: [
        '1 trained Vedic pandit',
        '1 complete Durga Saptashati Paath',
        'Essential pooja samagri and havan set up',
        'Flowers and fruits',
        'Dakshina and pandit travel charges'
      ],
      ritualSteps: [
        'Ganesh Poojan',
        'Sankalp',
        'Complete Saptashati Paath',
        'Havan and Aarti'
      ],
      bestFor: ['Emotional reassurance', 'Individual strength', 'Focused Shakti invocation'],
      // // languages: ['Hindi', 'Sanskrit'],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
      whatThisFeelsLike: [
        'A steady, devotional invocation seeking protection and emotional balance.',
        'Mother Durga\'s fierce compassion filling you with courage',
        'Ancient feminine power awakening inner strength',
        'Feeling fearless and divinely protected in all endeavors'
      ]
    },
    {
      id: 'standard',
      name: 'Standard Durga Saptshati',
      price: '₹18,000',
      originalPrice: '₹22,000',
      duration: '4-5 hours',
      features: [
        'Complete 700 verse recitation',
        'Traditional ceremonial setup',
        'Experienced priests',
        'Sacred fire ceremony',
        'Divine protection mantras'
      ],
      popular: true,
      priests: 3,
      materials: [
        'Premium sacred texts',
        'Fresh red flowers',
        'Pure kumkum and haldi',
        'Sacred fire materials',
        'High-quality incense',
        'Traditional brass items'
      ],
      includes: [
        '2 pandits + 1 Senior Acharya',
        '3 complete Saptashati recitations by 3 pandits, i.e., 9 recitations.',
        'Senior Acharya supervision',
        'Expanded sankalp (individual + family intent)',
        'Pre- and post-pooja guidance',
        'Essential pooja samagri and Havan set up',
        'Flowers and fruits',
        'Dakshina and pandit travel charges'
      ],
      ritualSteps: [
        'Ganesh Poojan and invocation',
        'Detailed sankalp',
        'CMulti-cycle Saptashati Paath',
        'Havan, Aarti and blessings',
        'Aarti',
        'Divine blessings ceremony'
      ],
      bestFor: ['Family-level concerns', 'Repeated challenges', 'Periods of transition', 'Spiritual strength'],
      // // languages: ['Sanskrit', 'Hindi', 'English'],
      specialFeatures: [
        'Complete 700 verse recitation',
        'Sacred fire offerings',
        'Divine mother blessings'
      ],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.'
    },
    {
      id: 'premium',
      name: 'Premium Durga Saptshati',
      price: '₹28,000',
      originalPrice: '₹35,000',
      duration: '6 hours',
      features: [
        'Grand ceremonial setup',
        'Master priest guidance',
        'Extended sacred fire ceremony',
        'Complete divine protection ritual',
        'Family feast included'
      ],
      priests: 5,
      materials: [
        'Luxury ceremonial items',
        'Premium flowers and garlands',
        'Silver puja vessels',
        'Sacred fire altar',
        'Exotic incense varieties',
        'Traditional silk decorations'
      ],
      includes: [
        '3 pandits + 1 Senior Acharya',
        'Saptashati recitations on all 9 days of Navratri.',
        'Senior Kashi-trained Acharya leading',
        'Detailed, purpose-specific sankalp',
        'Comprehensive pre- and post-pooja guidance',
        'Essential pooja samagri and Havan set up',
        'Dakshina and pandit travel charges'
      ],
      ritualSteps: [
        'Detailed sankalp and invocation',
        'Extended Saptashati Paath for 9 days',
        'Havan, Aarti and Shanti Paath',
        'Extended sacred fire ceremony',
        'Special protection mantras',
        'Grand aarti with music',
        'Family blessing ceremony'
      ],
      bestFor: ['Long-standing emotional or karmic challenges', 'Deep Shakti invocation', 'Serious Devi sadhana', 'Life transformation'],
      // // languages: ['Sanskrit', 'Hindi', 'English'],
      specialFeatures: [
        'Master priest with 25+ years experience',
        'Extended sacred fire ceremony',
        'Traditional devotional music',
        'Complete family involvement'
      ],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.'
    }
  ],
  'sat-chandi-yagya': [
    {
      id: 'basic',
      name: 'Basic Sat Chandi',
      price: '₹35,000',
      originalPrice: '₹42,000',
      duration: '4 hours',
      features: [
        'Essential Chandi recitation',
        'Basic fire ceremony',
        'Qualified priests',
        'Sacred offerings'
      ],
      priests: 3,
      materials: [
        'Sacred fire setup',
        'Basic ghee and offerings',
        'Traditional flowers',
        'Sacred herbs',
        'Simple yagya materials',
        'Essential grains'
      ],
      includes: [
        '3 qualified priests',
        'Basic yagya setup',
        'Essential sacred offerings',
        'Simple fire ceremony',
        'Basic prasadam'
      ],
      ritualSteps: [
        'Fire altar preparation',
        'Devi invocation',
        'Essential Chandi path',
        'Basic fire offerings',
        'Simple purnahuti'
      ],
      bestFor: ['Basic divine blessings', 'Spiritual cleansing', 'Personal growth'],
      // // languages: ['Hindi', 'Sanskrit'],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.'
    },
    {
      id: 'standard',
      name: 'Standard Sat Chandi Yagya',
      price: '₹45,000',
      originalPrice: '₹55,000',
      duration: '6-8 hours',
      features: [
        'Complete fire sacrifice',
        '700 sacred offerings',
        'Experienced priests',
        'Full ceremonial setup',
        'Divine protection rituals'
      ],
      popular: true,
      priests: 5,
      materials: [
        'Grand fire altar',
        'Premium ghee and oils',
        'Sacred herbs collection',
        'Precious gemstone offerings',
        'Traditional yagya materials',
        'Pure grains and flowers'
      ],
      includes: [
        '5 qualified priests',
        'Complete yagya setup',
        '700 sacred offerings',
        'Full divine fire ceremony',
        'Blessed prasadam',
        'Digital documentation'
      ],
      ritualSteps: [
        'Elaborate fire altar setup',
        'Complete Devi invocation',
        'Full Chandi path recitation',
        '700 sacred fire offerings',
        'Grand purnahuti ceremony',
        'Divine blessings ritual'
      ],
      bestFor: ['Major life challenges', 'Business success', 'Divine intervention', 'Spiritual advancement'],
      // // languages: ['Sanskrit', 'Hindi', 'English'],
      specialFeatures: [
        '700 sacred fire offerings',
        'Powerful divine intervention',
        'Complete protection mantras'
      ],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.'
    },
    {
      id: 'premium',
      name: 'Premium Sat Chandi Yagya',
      price: '₹75,000',
      originalPrice: '₹90,000',
      duration: '10 hours',
      features: [
        'Grand fire sacrifice ceremony',
        'Master priest guidance',
        'Extended divine rituals',
        'Royal ceremonial setup',
        'Complete family involvement',
        'Traditional feast included'
      ],
      priests: 7,
      materials: [
        'Royal fire altar setup',
        'Premium sacred materials',
        'Precious metal vessels',
        'Rare herbs and gems',
        'Luxury ceremonial items',
        'Traditional decorations'
      ],
      includes: [
        '7 master priests',
        'Royal yagya setup',
        'Extended fire ceremony',
        'Professional photography',
        'Grand family feast',
        'Blessed yantra collection',
        'Sacred ash for family'
      ],
      ritualSteps: [
        'Royal fire altar consecration',
        'Grand Devi invocation ceremony',
        'Complete Chandi path with commentary',
        'Extended 700+ offerings',
        'Special divine intervention rituals',
        'Grand purnahuti with music',
        'Family blessing and feast'
      ],
      bestFor: ['Ultimate divine blessings', 'Major life transformation', 'Business empire building', 'Spiritual mastery'],
      // // languages: ['Sanskrit', 'Hindi', 'English'],
      specialFeatures: [
        'Master priests with 30+ years experience',
        'Royal ceremonial setup',
        'Traditional devotional orchestra',
        'Complete divine protection for family'
      ],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.'
    }
  ],
  'baglamukhi-path': [
    {
      id: 'basic',
      name: 'Basic Baglamukhi Path',
      price: '₹18,000',
      originalPrice: '₹22,000',
      duration: '2 hours',
      features: [
        'Essential Baglamukhi mantras',
        'Basic victory rituals',
        'Qualified priests',
        'Enemy suppression prayers'
      ],
      priests: 1,
      materials: [
        'Yellow flowers',
        'Turmeric powder',
        'Yellow cloth',
        'Basic Baglamukhi yantra',
        'Mustard oil',
        'Simple oil lamps'
      ],
      includes: [
        '1 qualified priest',
        'Basic Baglamukhi worship',
        'Essential victory mantras',
        'Simple yantra consecration',
        'Basic prasadam'
      ],
      ritualSteps: [
        'Ganesh Puja',
        'Baglamukhi invocation',
        'Essential victory mantras',
        'Simple yantra worship',
        'Victory prayers'
      ],
      bestFor: ['Basic enemy protection', 'Simple legal matters', 'Personal conflicts'],
      // // languages: ['Hindi', 'Sanskrit'],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.'
    },
    {
      id: 'standard',
      name: 'Standard Baglamukhi Path',
      price: '₹22,000',
      originalPrice: '₹28,000',
      duration: '3-4 hours',
      features: [
        'Complete Baglamukhi worship',
        'Victory over enemies',
        'Legal success mantras',
        'Sacred fire ceremony',
        'Powerful protection rituals'
      ],
      popular: true,
      priests: 2,
      materials: [
        'Premium yellow flowers',
        'Pure turmeric',
        'Sacred yellow silk',
        'Consecrated Baglamukhi yantra',
        'Sacred mustard oil',
        'Traditional brass items'
      ],
      includes: [
        '2 experienced priests',
        'Complete Baglamukhi worship',
        'Victory mantras recitation',
        'Sacred fire ceremony',
        'Blessed yantra for home',
        'Success prasadam'
      ],
      ritualSteps: [
        'Elaborate Ganesh Puja',
        'Complete Baglamukhi invocation',
        'Powerful victory mantras',
        'Sacred yantra consecration',
        'Fire ceremony for victory',
        'Enemy suppression rituals'
      ],
      bestFor: ['Victory over enemies', 'Legal matters success', 'Business competition', 'Court case victory'],
      // // languages: ['Sanskrit', 'Hindi', 'English'],
      specialFeatures: [
        'Powerful enemy suppression mantras',
        'Legal victory blessings',
        'Sacred fire ceremony'
      ],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.'
    },
    {
      id: 'premium',
      name: 'Premium Baglamukhi Path',
      price: '₹35,000',
      originalPrice: '₹45,000',
      duration: '5 hours',
      features: [
        'Grand Baglamukhi ceremony',
        'Master priest guidance',
        'Extended victory rituals',
        'Complete enemy suppression',
        'Legal victory guarantee mantras'
      ],
      priests: 3,
      materials: [
        'Luxury yellow decorations',
        'Premium sacred materials',
        'Gold-plated yantra',
        'Sacred mustard oil collection',
        'Traditional brass vessels',
        'Exotic yellow flowers'
      ],
      includes: [
        '3 master priests',
        'Grand ceremonial setup',
        'Extended victory mantras',
        'Powerful yantra consecration',
        'Professional documentation',
        'Blessed protection amulets',
        'Victory success kit'
      ],
      ritualSteps: [
        'Grand Ganesh Puja ceremony',
        'Elaborate Baglamukhi invocation',
        'Extended powerful victory mantras',
        'Grand yantra consecration',
        'Extended fire ceremony',
        'Complete enemy suppression ritual',
        'Victory guarantee blessings'
      ],
      bestFor: ['Major legal battles', 'Business rivalry victory', 'Political success', 'Complete enemy defeat'],
      // // languages: ['Sanskrit', 'Hindi', 'English'],
      specialFeatures: [
        'Master priest with specialized training',
        'Extended victory guarantee mantras',
        'Complete enemy suppression ritual',
        'Success guarantee blessings'
      ],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.'
    }
  ],
  'shani-shanti-pujan': [
    {
      id: 'basic',
      name: 'Basic Shani Shanti',
      price: '₹15,000',
      originalPrice: '₹18,000',
      duration: '3 Days',
      features: [
        'Essential Shani mantras',
        'Basic ritual setup',
        'Traditional offerings',
        'Saturn pacification'
      ],
      priests: 2,
      materials: [
        'Black sesame seeds',
        'Mustard oil',
        'Basic iron items',
        'Black cloth',
        'Simple oil lamps',
        'Black flowers'
      ],
      includes: [
        '1 qualified priest',
        'Basic puja materials',
        'Simple Shani yantra',
        'Essential mantras',
        'Basic prasadam'
      ],
      ritualSteps: [
        'Ganesh Invocation',
        'Simple Shani Kalash setup',
        'Basic Shani mantras',
        'Oil offerings',
        'Simple aarti'
      ],
      bestFor: ['Minor career obstacles', 'Basic Saturn troubles', 'Simple delays'],
      // // languages: ['Hindi', 'Sanskrit'],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
      whatThisFeelsLike: [
        'Heavy burdens lifting from your shoulders as Saturn\'s lessons transform',
        'Patience and perseverance growing stronger within you',
        'Justice and fairness flowing back into your life situations',
        'Gradual but steady improvement in career and life circumstances'
      ],
      jaapCount: '23000 Shani Mantra Jaap',
      highlightTags: ['Senior Acharya', 'All Pooja Samagri included', 'Havan included', 'Option to Participate in person at our site for Sankalp & Havan', 'Online Sankalp', 'Free pickup & Drop facility from your hotel/place in Varanasi to our site', 'Prashadam delivered to your home anywhere in World', 'Prasadam delivery times may vary based on location'],
      
    },
    {
      id: 'standard',
      name: 'Standard Shani Shanti',
      price: '₹18,000',
      originalPrice: '₹22,000',
      duration: '5 Days',
      features: [
        'Complete Shani worship',
        'Traditional setup',
        'Iron donation ceremony',
        'Saturn obstacle removal',
        'Protection mantras'
      ],
      popular: true,
      priests: 4,
      materials: [
        'Premium black sesame',
        'Pure mustard oil',
        'Traditional iron items',
        'Sacred black cloth',
        'Blue flowers',
        'Shani-specific offerings'
      ],
      includes: [
        '2 experienced priests',
        'Complete puja materials',
        'Energized Shani yantra',
        'Iron donation guidance',
        'Blessed prasadam',
        'Protection tilak'
      ],
      ritualSteps: [
        'Elaborate Ganesh Puja',
        'Traditional Shani Kalash setup',
        'Complete Shani mantra recitation',
        'Oil abhishekam ceremony',
        'Iron donation ritual',
        'Protection aarti'
      ],
      bestFor: ['Career obstacles', 'Legal issues', 'Financial problems', 'Marriage delays'],
      // // languages: ['Sanskrit', 'Hindi', 'English'],
      specialFeatures: [
        'Traditional Saturn mantras',
        'Iron donation ceremony',
        'Black sesame offerings'
      ],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
      whatThisFeelsLike: [
        ' A stronger collective invocation with deeper mantra resonance and guidance.'
      ],
      jaapCount: '46000 Shani Mantra Jaap',
      highlightTags: ['1 Senior Acharya', 'All Pooja Samagri included', 'Havan with sammi wood included', 'Option to Participate in person at our site for Sankalp & Havan', 'Online Sankalp', 'Free pickup & Drop facility from your hotel/place in Varanasi to our site', 'Prashadam delivered to your home anywhere in World', 'Prasadam delivery times may vary based on location'],
      
    },
    {
      id: 'premium',
      name: 'Premium Shani Shanti',
      price: '₹28,000',
      originalPrice: '₹35,000',
      duration: '7 Days',
      features: [
        'Grand Shani ceremony',
        'Extended rituals',
        'Complete obstacle removal',
        'Long-term protection',
        'Multiple donations'
      ],
      priests: 7,
      materials: [
        'Luxury ceremonial items',
        'Premium iron artifacts',
        'Sacred black gemstones',
        'Traditional brass vessels',
        'Expensive mustard oil',
        'Royal blue flowers'
      ],
      includes: [
        '3 master priests',
        'Grand ceremonial setup',
        'Multiple Shani yantras',
        'Extended iron donations',
        'Complete protection kit',
        'Lifetime yantra maintenance'
      ],
      ritualSteps: [
        'Grand Ganesh Puja ceremony',
        'Elaborate Shani Kalash installation',
        'Extended Shani mantra recitation',
        'Multiple abhishekam ceremonies',
        'Grand iron donation ritual',
        'Complete protection ceremony'
      ],
      bestFor: ['Major Saturn dasha', 'Severe life obstacles', 'Long-term problems', 'Complete transformation'],
      // // languages: ['Sanskrit', 'Hindi', 'English'],
      specialFeatures: [
        'Master priest with Saturn expertise',
        'Extended protection mantras',
        'Multiple donation ceremonies',
        'Lifetime spiritual guidance'
      ],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
      whatThisFeelsLike: [
        ' A deeply immersive graha anushthan performed with sustained discipline and senior Acharya oversight.'
      ],
      jaapCount: 'Upto 125,000 Shani Mantra Jaap',
      highlightTags: ['1008 Maha Mritunjay Mantra Jaap', '1 Senior Acharya', 'Havan with sammi wood included', 'Complementary Shani Kavacham', 'Senior Acharya', 'All Pooja Samagri included', 'Havan included', 'Online Sankalp', 'Option to Participate in person at our site for Sankalp & Havan',  'Free pickup & Drop facility from your hotel/place in Varanasi to our site', 'Prashadam & Rudraksha Mala delivered to your home anywhere in World', 'Prasadam delivery times may vary based on location'],
      
    }
  ],
  'mangal-shanti-pujan': [
    {
      id: 'basic',
      name: 'Basic Mangal Shanti',
      price: '₹12,000',
      originalPrice: '₹15,000',
      duration: '1.5 hours',
      features: [
        'Essential Mars mantras',
        'Basic manglik remedies',
        'Simple coral blessings',
        'Traditional offerings'
      ],
      priests: 1,
      materials: [
        'Red coral (basic)',
        'Red flowers',
        'Simple jaggery',
        'Red cloth',
        'Basic copper items',
        'Red rose petals'
      ],
      includes: [
        '1 qualified priest',
        'Basic Mars worship',
        'Simple manglik remedies',
        'Red coral blessing',
        'Basic prasadam'
      ],
      ritualSteps: [
        'Ganesh Puja',
        'Basic Mangal invocation',
        'Essential Mars mantras',
        'Red flower offerings',
        'Simple aarti'
      ],
      bestFor: ['Minor manglik dosha', 'Basic Mars troubles', 'Simple courage building'],
      // // languages: ['Hindi', 'Sanskrit'],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
      whatThisFeelsLike: [
        ' A focused planetary balancing ritual addressing specific graha influences with disciplined mantra chanting.'
      ],
      jaapCount: '10000 Mangal Mantra Jaap',
      highlightTags: ['Senior Acharya', 'All Pooja Samagri included', 'Kashi Acharya ji Dakshina & expenses included', 'Havan included', 'Option to Participate in person at our site for Sankalp & Havan', 'Online Sankalp', 'Free pickup & Drop facility from your hotel/place in Varanasi to our site', 'Prashadam delivered to your home anywhere in World', 'Prasadam delivery times may vary based on location'],
      
    },
    {
      id: 'standard',
      name: 'Standard Mangal Shanti',
      price: '₹15,000',
      originalPrice: '₹18,000',
      duration: '2-3 hours',
      features: [
        'Complete Mars worship',
        'Manglik dosha removal',
        'Red coral energizing',
        'Courage enhancement',
        'Marriage obstacle removal'
      ],
      popular: true,
      priests: 2,
      materials: [
        'Premium red coral',
        'Fresh red flowers',
        'Pure jaggery',
        'Sacred red silk',
        'Traditional copper vessels',
        'Mars-specific offerings'
      ],
      includes: [
        '2 qualified priests',
        'Complete puja materials',
        'Energized red coral',
        'Manglik dosha remedies',
        'Sweet prasadam',
        'Marriage blessing'
      ],
      ritualSteps: [
        'Elaborate Ganesh Puja',
        'Complete Mangal Kalash setup',
        'Full Mars mantra chanting',
        'Red coral consecration',
        'Courage building rituals',
        'Marriage blessing aarti'
      ],
      bestFor: ['Manglik dosha', 'Marriage delays', 'Courage building', 'Property matters'],
      // // languages: ['Sanskrit', 'Hindi', 'English'],
      specialFeatures: [
        'Manglik dosha remedies',
        'Red coral energizing',
        'Marriage compatibility blessings'
      ],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
      whatThisFeelsLike: [
        ' A stronger collective invocation with deeper mantra resonance and guidance.'
      ],
      jaapCount: '20000 Mangal Mantra Jaap',
      highlightTags: ['1 Senior Acharya', 'All Pooja Samagri included', 'Kashi Acharya ji Dakshina & expenses included', 'Havan included', 'Option to Participate in person at our site for Sankalp & Havan', 'Online Sankalp', 'Free pickup & Drop facility from your hotel/place in Varanasi to our site', 'Prashadam delivered to your home anywhere in World', 'Prasadam delivery times may vary based on location'],
      
    },
    {
      id: 'premium',
      name: 'Premium Mangal Shanti',
      price: '₹25,000',
      originalPrice: '₹32,000',
      duration: '4 hours',
      features: [
        'Grand Mars ceremony',
        'Complete manglik solution',
        'Premium coral consecration',
        'Extended protection rituals',
        'Marriage guarantee blessings'
      ],
      priests: 3,
      materials: [
        'High-quality red coral',
        'Exotic red flowers',
        'Premium jaggery varieties',
        'Luxury red silk',
        'Golden copper vessels',
        'Precious red gemstones'
      ],
      includes: [
        '3 master priests',
        'Grand ceremonial setup',
        'Premium coral collection',
        'Complete manglik solutions',
        'Extended marriage blessings',
        'Lifetime protection guidance'
      ],
      ritualSteps: [
        'Grand Ganesh Puja ceremony',
        'Elaborate Mangal Kalash installation',
        'Extended Mars mantra recitation',
        'Premium coral consecration ritual',
        'Complete manglik dosha removal',
        'Grand marriage blessing ceremony'
      ],
      bestFor: ['Severe manglik dosha', 'Major marriage obstacles', 'Complete Mars pacification', 'Property disputes'],
      // // languages: ['Sanskrit', 'Hindi', 'English'],
      specialFeatures: [
        'Master priest with Mars expertise',
        'Guaranteed manglik solutions',
        'Premium coral consecration',
        'Complete marriage compatibility'
      ],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
      whatThisFeelsLike: [
        ' A deeply immersive graha anushthan performed with sustained discipline and senior Acharya oversight.'
      ],
      jaapCount: '40000 Mangal Mantra Jaap',
      highlightTags: ['1 Senior Acharya', 'Kashi Acharya ji Dakshina & expenses included', 'Pre consultation with senior Acharya',  'All Pooja Samagri included', 'Havan included', 'Online Sankalp', 'Option to Participate in person at our site for Sankalp & Havan',  'Free pickup & Drop facility from your hotel/place in Varanasi to our site', 'Prashadam delivered to your home anywhere in World', 'Prasadam delivery times may vary based on location'],
    }
  ],
  'guru-shanti-pujan': [
    {
      id: 'basic',
      name: 'Basic Guru Shanti',
      price: '₹16,000',
      originalPrice: '₹20,000',
      duration: '3 Days',
      features: [
        'Essential Jupiter mantras',
        'Basic wisdom rituals',
        'Simple educational blessings',
        'Traditional offerings'
      ],
      priests: 2,
      materials: [
        'Basic yellow topaz',
        'Turmeric powder',
        'Yellow flowers',
        'Simple gold items',
        'Fresh bananas',
        'Yellow cloth'
      ],
      includes: [
        '2 qualified priests',
        'Basic Jupiter worship',
        'Simple Guru yantra',
        'Educational blessings',
        'Basic prasadam'
      ],
      ritualSteps: [
        'Ganesh Puja',
        'Basic Guru invocation',
        'Essential Jupiter mantras',
        'Yellow offerings',
        'Wisdom prayers'
      ],
      bestFor: ['Basic education needs', 'Simple wisdom seeking', 'Student blessings'],
      // // languages: ['Hindi', 'Sanskrit'],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
      whatThisFeelsLike: [
        ' A focused planetary balancing ritual addressing specific graha influences with disciplined mantra chanting.'
      ],
      jaapCount: '19,000 Guru Mantra Jaap',
      highlightTags: ['Senior Acharya', 'All Pooja Samagri included', 'Havan included', 'Option to Participate in person at our site for Sankalp & Havan', 'Online Sankalp', 'Free pickup & Drop facility from your hotel/place in Varanasi to our site', 'Prashadam delivered to your home anywhere in World', 'Prasadam delivery times may vary based on location'],
      
    },
    {
      id: 'standard',
      name: 'Standard Guru Shanti',
      price: '₹20,000',
      originalPrice: '₹25,000',
      duration: '4-5 Days',
      features: [
        'Complete Jupiter worship',
        'Wisdom enhancement',
        'Educational success',
        'Career advancement',
        'Spiritual growth'
      ],
      popular: true,
      priests: 3,
      materials: [
        'Premium yellow topaz',
        'Pure turmeric',
        'Fresh yellow flowers',
        'Traditional gold items',
        'Sacred banana offerings',
        'Jupiter-specific materials'
      ],
      includes: [
        '3 learned priests',
        'Complete puja materials',
        'Energized Guru yantra',
        'Wisdom enhancement rituals',
        'Educational guidance',
        'Career blessing'
      ],
      ritualSteps: [
        'Elaborate Ganesh Puja',
        'Complete Guru Kalash setup',
        'Full Jupiter mantra recitation',
        'Yellow topaz consecration',
        'Knowledge enhancement prayers',
        'Wisdom blessing aarti'
      ],
      bestFor: ['Education success', 'Career advancement', 'Spiritual growth', 'Teacher-student relationships'],
      // // languages: ['Sanskrit', 'Hindi', 'English'],
      specialFeatures: [
        'Educational success mantras',
        'Wisdom enhancement rituals',
        'Yellow topaz blessings'
      ],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
      whatThisFeelsLike: [
        ' A stronger collective invocation with deeper mantra resonance and guidance.'
      ],
      jaapCount: '38000 Guru Mantra Jaap',
      highlightTags: ['1 Senior Acharya', 'All Pooja Samagri included', 'Havan included', 'Option to Participate in person at our site for Sankalp & Havan', 'Online Sankalp', 'Free pickup & Drop facility from your hotel/place in Varanasi to our site', 'Prashadam delivered to your home anywhere in World', 'Prasadam delivery times may vary based on location'],
    },
    {
      id: 'premium',
      name: 'Premium Guru Shanti',
      price: '₹35,000',
      originalPrice: '₹45,000',
      duration: '5-7 Days',
      features: [
        'Grand Jupiter ceremony',
        'Complete wisdom transformation',
        'Educational mastery',
        'Spiritual enlightenment',
        'Guru-disciple connection'
      ],
      priests: 4,
      materials: [
        'High-grade yellow topaz',
        'Sacred turmeric varieties',
        'Exotic yellow flowers',
        'Premium gold vessels',
        'Sacred banana varieties',
        'Rare educational herbs'
      ],
      includes: [
        '4 master priests (learned scholars)',
        'Grand educational setup',
        'Premium Guru yantra collection',
        'Complete wisdom transformation',
        'Educational mastery guidance',
        'Lifetime spiritual mentorship'
      ],
      ritualSteps: [
        'Grand Ganesh Puja ceremony',
        'Elaborate Guru Kalash installation',
        'Extended Jupiter wisdom mantras',
        'Premium topaz consecration',
        'Complete knowledge enhancement',
        'Grand enlightenment ceremony'
      ],
      bestFor: ['Academic excellence', 'Professional mastery', 'Spiritual enlightenment', 'Research success'],
      // // languages: ['Sanskrit', 'Hindi', 'English'],
      specialFeatures: [
        'Master scholars as priests',
        'Complete educational transformation',
        'Lifetime wisdom guidance',
        'Research and innovation blessings'
      ],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
      whatThisFeelsLike: [
        ' A deeply immersive graha anushthan performed with sustained discipline and senior Acharya oversight.'
      ],
      jaapCount: 'Upto 76000 Guru Mantra Jaap',
      highlightTags: ['1 Senior Acharya', 'Kashi Acharya Dakshina & expenses included', 'Pre consultation with senior Acharya',  'All Pooja Samagri included', 'Havan included', 'Online Sankalp', 'Option to Participate in person at our site for Sankalp & Havan',  'Free pickup & Drop facility from your hotel/place in Varanasi to our site', 'Prashadam delivered to your home anywhere in World', 'Prasadam delivery times may vary based on location'],
    }
  ],
  'rahu-shanti-pujan': [
    {
      id: 'basic',
      name: 'Basic Rahu Shanti',
      price: '₹18,000',
      originalPrice: '₹22,000',
      duration: '3 days',
      features: [
        'Essential Rahu mantras',
        'Basic confusion removal',
        'Simple clarity rituals',
        'Traditional offerings'
      ],
      priests: 2,
      materials: [
        'Basic black gram',
        'Simple coconut',
        'Blue flowers',
        'Basic hessonite',
        'Traditional incense',
        'Black cloth'
      ],
      includes: [
        '2 qualified priests',
        'Basic Rahu worship',
        'Simple Rahu yantra',
        'Confusion removal prayers',
        'Basic prasadam'
      ],
      ritualSteps: [
        'Ganesh Puja',
        'Basic Rahu invocation',
        'Essential Rahu mantras',
        'Black gram offerings',
        'Clarity prayers'
      ],
      bestFor: ['Minor mental confusion', 'Basic Rahu troubles', 'Simple clarity needs'],
      // // languages: ['Hindi', 'Sanskrit'],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
      whatThisFeelsLike: [
        ' A focused planetary balancing ritual addressing specific graha influences with disciplined mantra chanting.'
      ],
      jaapCount: '18000 Rahu Mantra Jaap',
      highlightTags: ['Senior Acharya', 'All Pooja Samagri included', 'Havan included', 'Option to Participate in person at our site for Sankalp & Havan', 'Online Sankalp', 'Free pickup & Drop facility from your hotel/place in Varanasi to our site'],

    },
    {
      id: 'standard',
      name: 'Standard Rahu Shanti',
      price: '₹22,000',
      originalPrice: '₹28,000',
      duration: '3-5 Days',
      features: [
        'Complete Rahu worship',
        'Confusion removal',
        'Mental clarity enhancement',
        'Foreign travel blessings',
        'Technology problem solutions'
      ],
      popular: true,
      priests: 3,
      materials: [
        'Premium black gram',
        'Sacred coconuts',
        'Fresh blue flowers',
        'Quality hessonite garnet',
        'Special Rahu incense',
        'Sacred blue silk'
      ],
      includes: [
        '3 experienced priests',
        'Complete puja materials',
        'Energized Rahu yantra',
        'Mental clarity rituals',
        'Technology blessing',
        'Travel protection'
      ],
      ritualSteps: [
        'Extended Ganesh Puja',
        'Complete Rahu Kalash setup',
        'Full Rahu mantra recitation',
        'Hessonite consecration',
        'Confusion removal ceremony',
        'Clarity blessing aarti'
      ],
      bestFor: ['Mental confusion', 'Sudden problems', 'Foreign travel', 'Technology issues'],
      // // languages: ['Sanskrit', 'Hindi', 'English'],
      specialFeatures: [
        'Confusion removal mantras',
        'Hessonite energizing',
        'Mental clarity rituals'
      ],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
      whatThisFeelsLike: [
        ' A stronger collective invocation with deeper mantra resonance and guidance.'
      ],
     jaapCount: '36000 Rahu Mantra Jaap',
      highlightTags: ['1 Senior Acharya', 'All Pooja Samagri included', 'Kashi Acharya ji Dakshina & expenses included', 'Havan included', 'Option to Participate in person at our site for Sankalp & Havan', 'Online Sankalp', 'Free pickup & Drop facility from your hotel/place in Varanasi to our site', 'Prashadam delivered to your home anywhere in World', 'Prasadam delivery times may vary based on location'],

    },
    {
      id: 'premium',
      name: 'Premium Rahu Shanti',
      price: '₹38,000',
      originalPrice: '₹48,000',
      duration: '5-7 Days',
      features: [
        'Grand Rahu ceremony',
        'Complete confusion elimination',
        'Advanced mental clarity',
        'International success',
        'Technology mastery'
      ],
      priests: 4,
      materials: [
        'High-quality hessonite',
        'Premium black gram varieties',
        'Exotic blue flowers',
        'Sacred coconut collection',
        'Rare Rahu herbs',
        'Luxury blue decorations'
      ],
      includes: [
        '4 master priests',
        'Grand ceremonial setup',
        'Premium Rahu yantra collection',
        'Complete mental transformation',
        'International success guidance',
        'Technology mastery blessings'
      ],
      ritualSteps: [
        'Grand Ganesh Puja ceremony',
        'Elaborate Rahu Kalash installation',
        'Extended Rahu mantra recitation',
        'Premium hessonite consecration',
        'Complete confusion elimination',
        'Grand clarity blessing ceremony'
      ],
      bestFor: ['Severe mental issues', 'International ventures', 'Technology innovation', 'Complete Rahu pacification'],
      // // languages: ['Sanskrit', 'Hindi', 'English'],
      specialFeatures: [
        'Master priests with Rahu expertise',
        'Complete mental transformation',
        'International success guarantee',
        'Technology innovation blessings'
      ],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
      whatThisFeelsLike: [
        ' A deeply immersive graha anushthan performed with sustained discipline and senior Acharya oversight.'
      ],
      jaapCount: 'Upto 72,000 Rahu Mantra Jaap',
      highlightTags: ['1 Senior Acharya', 'Kashi Acharya ji Dakshina & expenses included', 'Pre consultation with senior Acharya',  'All Pooja Samagri included', 'Havan included', 'Online Sankalp', 'Option to Participate in person at our site for Sankalp & Havan',  'Free pickup & Drop facility from your hotel/place in Varanasi to our site', 'Prashadam delivered to your home anywhere in World', 'Prasadam delivery times may vary based on location'],
    }
  ],
  'ketu-shanti-pujan': [
    {
      id: 'basic',
      name: 'Basic Ketu Shanti',
      price: '₹16,000',
      originalPrice: '₹20,000',
      duration: '2 Days',
      features: [
        'Essential Ketu mantras',
        'Basic spiritual cleansing',
        'Simple detachment blessings',
        'Traditional offerings'
      ],
      priests: 2,
      materials: [
        'Basic cat\'s eye stone',
        'White flowers',
        'Simple milk products',
        'White cloth',
        'Traditional lamps',
        'Basic herbs'
      ],
      includes: [
        '2 qualified priests',
        'Basic Ketu worship',
        'Simple Ketu yantra',
        'Spiritual cleansing rituals',
        'Basic prasadam'
      ],
      ritualSteps: [
        'Ganesh Puja',
        'Basic Ketu invocation',
        'Essential Ketu mantras',
        'White flower offerings',
        'Detachment prayers'
      ],
      bestFor: ['Minor spiritual confusion', 'Basic detachment needs', 'Simple cleansing'],
      // // languages: ['Hindi', 'Sanskrit'],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
      whatThisFeelsLike: [
        ' A focused planetary balancing ritual addressing specific graha influences with disciplined mantra chanting.'
      ],
      jaapCount: '17,000 Ketu Mantra Jaap',
      highlightTags: ['Senior Acharya', 'All Pooja Samagri included', 'Kashi Acharya ji Dakshina & expenses included', 'Havan included', 'Option to Participate in person at our site for Sankalp & Havan', 'Online Sankalp', 'Free pickup & Drop facility from your hotel/place in Varanasi to our site', 'Prashadam delivered to your home anywhere in World', 'Prasadam delivery times may vary based on location'],
    },
    {
      id: 'standard',
      name: 'Standard Ketu Shanti',
      price: '₹20,000',
      originalPrice: '₹25,000',
      duration: '3-4 Days',
      features: [
        'Complete Ketu worship',
        'Spiritual liberation',
        'Past karma cleansing',
        'Moksha path guidance',
        'Psychic protection'
      ],
      popular: true,
      priests: 3,
      materials: [
        'Premium cat\'s eye',
        'Fresh white flowers',
        'Pure milk products',
        'Sacred white silk',
        'Special Ketu herbs',
        'Traditional incense'
      ],
      includes: [
        '3 experienced priests',
        'Complete puja materials',
        'Energized Ketu yantra',
        'Karma cleansing rituals',
        'Spiritual guidance',
        'Psychic protection'
      ],
      ritualSteps: [
        'Extended Ganesh Puja',
        'Complete Ketu Kalash setup',
        'Full Ketu mantra recitation',
        'Cat\'s eye consecration',
        'Karma cleansing ceremony',
        'Liberation blessing aarti'
      ],
      bestFor: ['Past karma issues', 'Spiritual growth', 'Psychic disturbances', 'Moksha seeking'],
      // // languages: ['Sanskrit', 'Hindi', 'English'],
      specialFeatures: [
        'Karma cleansing mantras',
        'Cat\'s eye energizing',
        'Spiritual liberation rituals'
      ],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
      whatThisFeelsLike: [
        ' A stronger collective invocation with deeper mantra resonance and guidance.'
      ],
      jaapCount: '34,000 Ketu Mantra Jaap',
      highlightTags: ['1 Senior Acharya', 'All Pooja Samagri included', 'Kashi Acharya ji Dakshina & expenses included', 'Havan included', 'Option to Participate in person at our site for Sankalp & Havan', 'Online Sankalp', 'Free pickup & Drop facility from your hotel/place in Varanasi to our site', 'Prashadam delivered to your home anywhere in World', 'Prasadam delivery times may vary based on location'],
    },
    {
      id: 'premium',
      name: 'Premium Ketu Shanti',
      price: '₹32,000',
      originalPrice: '₹42,000',
      duration: '5-7 Days',
      features: [
        'Grand Ketu ceremony',
        'Complete liberation ritual',
        'Past life karma clearing',
        'Advanced spiritual guidance',
        'Moksha path acceleration'
      ],
      priests: 4,
      materials: [
        'High-quality cat\'s eye',
        'Exotic white flowers',
        'Premium milk varieties',
        'Sacred white decorations',
        'Rare spiritual herbs',
        'Ancient ritual items'
      ],
      includes: [
        '4 master priests (spiritual guides)',
        'Grand ceremonial setup',
        'Premium Ketu yantra collection',
        'Complete liberation rituals',
        'Advanced spiritual counseling',
        'Lifetime moksha guidance'
      ],
      ritualSteps: [
        'Grand Ganesh Puja ceremony',
        'Elaborate Ketu Kalash installation',
        'Extended liberation mantras',
        'Premium cat\'s eye consecration',
        'Complete past life clearing',
        'Grand moksha blessing ceremony'
      ],
      bestFor: ['Deep spiritual seeking', 'Past life issues', 'Complete liberation', 'Moksha preparation'],
      // // languages: ['Sanskrit', 'Hindi', 'English'],
      specialFeatures: [
        'Master spiritual guides',
        'Past life karma clearing',
        'Complete moksha preparation',
        'Advanced spiritual techniques'
      ],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
      whatThisFeelsLike: [
        ' A deeply immersive graha anushthan performed with sustained discipline and senior Acharya oversight.'
      ],
      jaapCount: 'Upto 72,000 Ketu Mantra Jaap',
      highlightTags: ['1 Senior Acharya', 'Kashi Acharya ji Dakshina & expenses included', 'Pre consultation with senior Acharya',  'All Pooja Samagri included', 'Havan included', 'Online Sankalp', 'Option to Participate in person at our site for Sankalp & Havan',  'Free pickup & Drop facility from your hotel/place in Varanasi to our site', 'Prashadam delivered to your home anywhere in World', 'Prasadam delivery times may vary based on location'],
    }
  ],
  'budh-shanti-pujan': [
    {
      id: 'basic',
      name: 'Basic Budh Shanti',
      price: '₹13,000',
      originalPrice: '₹16,000',
      duration: '2 Days',
      features: [
        'Essential Mercury mantras',
        'Basic intelligence boost',
        'Simple communication help',
        'Traditional offerings'
      ],
      priests: 1,
      materials: [
        'Basic emerald chip',
        'Green flowers',
        'Simple green items',
        'Green cloth',
        'Basic herbs',
        'Traditional lamps'
      ],
      includes: [
        '1 qualified priest',
        'Basic Mercury worship',
        'Simple Budh yantra',
        'Communication blessings',
        'Basic prasadam'
      ],
      ritualSteps: [
        'Ganesh Puja',
        'Basic Budh invocation',
        'Essential Mercury mantras',
        'Green offerings',
        'Intelligence prayers'
      ],
      bestFor: ['Basic communication issues', 'Simple intelligence boost', 'Student help'],
      // // languages: ['Hindi', 'Sanskrit'],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
      whatThisFeelsLike: [
        ' A focused planetary balancing ritual addressing specific graha influences with disciplined mantra chanting.'
      ],
      jaapCount: '9000 Budh Mantra Jaap',
      highlightTags: ['Senior Acharya', 'All Pooja Samagri included', 'Havan included', 'Option to Participate in person at our site for Sankalp & Havan', 'Online Sankalp', 'Free pickup & Drop facility from your hotel/place in Varanasi to our site'],


    },
    {
      id: 'standard',
      name: 'Standard Budh Shanti',
      price: '₹16,000',
      originalPrice: '₹20,000',
      duration: '2-3 Days',
      features: [
        'Complete Mercury worship',
        'Intelligence enhancement',
        'Communication mastery',
        'Business success',
        'Learning acceleration'
      ],
      popular: true,
      priests: 2,
      materials: [
        'Quality emerald',
        'Fresh green flowers',
        'Premium green items',
        'Sacred green silk',
        'Mercury-specific herbs',
        'Traditional brass items'
      ],
      includes: [
        '2 learned priests',
        'Complete puja materials',
        'Energized Budh yantra',
        'Intelligence rituals',
        'Business blessings',
        'Learning enhancement'
      ],
      ritualSteps: [
        'Detailed Ganesh Puja',
        'Complete Budh Kalash setup',
        'Full Mercury mantra chanting',
        'Emerald consecration',
        'Intelligence enhancement prayers',
        'Communication blessing aarti'
      ],
      bestFor: ['Communication skills', 'Business success', 'Academic performance', 'Learning abilities'],
      // // languages: ['Sanskrit', 'Hindi', 'English'],
      specialFeatures: [
        'Intelligence enhancement mantras',
        'Emerald energizing',
        'Business success rituals'
      ],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
      whatThisFeelsLike: [
        ' A stronger collective invocation with deeper mantra resonance and guidance.'
      ],
      jaapCount: '18000 Budh Mantra Jaap',
      highlightTags: ['1 Senior Acharya', 'All Pooja Samagri included', 'Havan  included', 'Option to Participate in person at our site for Sankalp & Havan', 'Online Sankalp', 'Free pickup & Drop facility from your hotel/place in Varanasi to our site'],
      
    },
    {
      id: 'premium',
      name: 'Premium Budh Shanti',
      price: '₹26,000',
      originalPrice: '₹34,000',
      duration: '3-4 Days',
      features: [
        'Grand Mercury ceremony',
        'Complete intelligence transformation',
        'Master communication skills',
        'Business empire building',
        'Genius level enhancement'
      ],
      priests: 3,
      materials: [
        'High-grade emerald',
        'Exotic green flowers',
        'Premium green gemstones',
        'Luxury green decorations',
        'Rare intelligence herbs',
        'Golden brass vessels'
      ],
      includes: [
        '3 master priests (scholars)',
        'Grand educational setup',
        'Premium emerald collection',
        'Complete intelligence transformation',
        'Business mastery guidance',
        'Lifetime learning enhancement'
      ],
      ritualSteps: [
        'Grand Ganesh Puja ceremony',
        'Elaborate Budh Kalash installation',
        'Extended Mercury wisdom mantras',
        'Premium emerald consecration',
        'Complete intelligence transformation',
        'Grand communication mastery ceremony'
      ],
      bestFor: ['Genius development', 'Business mastery', 'Communication excellence', 'Learning disabilities'],
      // // languages: ['Sanskrit', 'Hindi', 'English'],
      specialFeatures: [
        'Master scholar priests',
        'Genius level enhancement',
        'Business empire guidance',
        'Complete communication mastery'
      ],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
      whatThisFeelsLike: [
        ' A deeply immersive graha anushthan performed with sustained discipline and senior Acharya oversight.'
      ],
      jaapCount: 'Upto 36000 Shani Mantra Jaap',
      highlightTags: ['1008 Maha Mritunjay Mantra Jaap', '1 Senior Acharya', 'Havan with sammi wood included', 'Complementary Shani Kavacham', 'Senior Acharya', 'All Pooja Samagri included', 'Havan included', 'Online Sankalp', 'Option to Participate in person at our site for Sankalp & Havan',  'Free pickup & Drop facility from your hotel/place in Varanasi to our site'],
    }
  ],
  'shukra-shanti-pujan': [
    {
      id: 'basic',
      name: 'Basic Shukra Shanti',
      price: '₹14,000',
      originalPrice: '₹17,000',
      duration: '2-3 Days',
      features: [
        'Essential Venus mantras',
        'Basic love blessings',
        'Simple beauty enhancement',
        'Traditional offerings'
      ],
      priests: 2,
      materials: [
        'Basic diamond chip',
        'White roses',
        'Simple silver items',
        'White cloth',
        'Basic cosmetics',
        'Traditional incense'
      ],
      includes: [
        '2 qualified priests',
        'Basic Venus worship',
        'Simple Shukra yantra',
        'Love and beauty blessings',
        'Sweet prasadam'
      ],
      ritualSteps: [
        'Ganesh Puja',
        'Basic Shukra invocation',
        'Essential Venus mantras',
        'White rose offerings',
        'Beauty enhancement prayers'
      ],
      bestFor: ['Basic love issues', 'Simple beauty enhancement', 'Relationship harmony'],
      // // languages: ['Hindi', 'Sanskrit'],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
      whatThisFeelsLike: [
        ' A focused planetary balancing ritual addressing specific graha influences with disciplined mantra chanting.'
      ],
      jaapCount: '16000 Shukra Mantra Jaap',
      highlightTags: ['Senior Acharya', 'All Pooja Samagri included', 'Kashi Acharya ji Dakshina & expenses included', 'Havan included', 'Option to Participate in person at our site for Sankalp & Havan', 'Online Sankalp', 'Free pickup & Drop facility from your hotel/place in Varanasi to our site', 'Prashadam delivered to your home anywhere in World', 'Prasadam delivery times may vary based on location'],
    },
    {
      id: 'standard',
      name: 'Standard Shukra Shanti',
      price: '₹17,000',
      originalPrice: '₹22,000',
      duration: '3-4 Days',
      features: [
        'Complete Venus worship',
        'Love and relationships',
        'Beauty and charm enhancement',
        'Artistic talents',
        'Luxury and comfort'
      ],
      popular: true,
      priests: 3,
      materials: [
        'Quality diamond',
        'Premium white roses',
        'Traditional silver items',
        'Sacred white silk',
        'Venus-specific cosmetics',
        'Luxury decorations'
      ],
      includes: [
        '3 experienced priests',
        'Complete puja materials',
        'Energized Shukra yantra',
        'Love enhancement rituals',
        'Beauty blessing ceremony',
        'Artistic talent activation'
      ],
      ritualSteps: [
        'Elaborate Ganesh Puja',
        'Complete Shukra Kalash setup',
        'Full Venus mantra recitation',
        'Diamond consecration',
        'Love and beauty enhancement',
        'Artistic blessing aarti'
      ],
      bestFor: ['Love relationships', 'Beauty enhancement', 'Artistic talents', 'Marriage harmony'],
      // // languages: ['Sanskrit', 'Hindi', 'English'],
      specialFeatures: [
        'Love enhancement mantras',
        'Diamond energizing',
        'Beauty and charm rituals'
      ],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
      whatThisFeelsLike: [
        ' A stronger collective invocation with deeper mantra resonance and guidance.'
      ],
      jaapCount: '32000 Shukra Mantra Jaap',
      highlightTags: ['1 Senior Acharya', 'All Pooja Samagri included', 'Kashi Acharya ji Dakshina & expenses included', 'Havan included', 'Option to Participate in person at our site for Sankalp & Havan', 'Online Sankalp', 'Free pickup & Drop facility from your hotel/place in Varanasi to our site', 'Prashadam delivered to your home anywhere in World', 'Prasadam delivery times may vary based on location'],
    },
    {
      id: 'premium',
      name: 'Premium Shukra Shanti',
      price: '₹30,000',
      originalPrice: '₹38,000',
      duration: '5-7 Days',
      features: [
        'Grand Venus ceremony',
        'Complete love transformation',
        'Ultimate beauty enhancement',
        'Artistic mastery',
        'Luxury lifestyle blessings'
      ],
      priests: 4,
      materials: [
        'High-quality diamond',
        'Exotic white flowers',
        'Premium silver vessels',
        'Luxury white decorations',
        'Rare beauty herbs',
        'Precious cosmetic items'
      ],
      includes: [
        '4 master priests',
        'Grand romantic setup',
        'Premium diamond collection',
        'Complete love transformation',
        'Ultimate beauty enhancement',
        'Luxury lifestyle guidance'
      ],
      ritualSteps: [
        'Grand Ganesh Puja ceremony',
        'Elaborate Shukra Kalash installation',
        'Extended Venus love mantras',
        'Premium diamond consecration',
        'Complete beauty transformation',
        'Grand love and luxury ceremony'
      ],
      bestFor: ['Complete love transformation', 'Ultimate beauty', 'Artistic mastery', 'Luxury lifestyle'],
      // // languages: ['Sanskrit', 'Hindi', 'English'],
      specialFeatures: [
        'Master priests with Venus expertise',
        'Complete love transformation',
        'Ultimate beauty enhancement',
        'Luxury lifestyle guarantee'
      ],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
      whatThisFeelsLike: [
        ' A deeply immersive graha anushthan performed with sustained discipline and senior Acharya oversight.'
      ],
      jaapCount: '72000 Shukra Mantra Jaap',
      highlightTags: ['1 Senior Acharya', 'All Pooja Samagri included', 'Kashi Acharya ji Dakshina & expenses included', 'Havan included', 'Option to Participate in person at our site for Sankalp & Havan', 'Online Sankalp', 'Free pickup & Drop facility from your hotel/place in Varanasi to our site', 'Prashadam delivered to your home anywhere in World', 'Prasadam delivery times may vary based on location']

    }
  ],
  'surya-shanti-pujan': [
    {
      id: 'basic',
      name: 'Basic Surya Shanti',
      price: '₹14,000',
      originalPrice: '₹18,000',
      duration: '2 Days',
      features: [
        'Essential Sun mantras',
        'Basic vitality enhancement',
        'Simple health blessings',
        'Traditional offerings'
      ],
      priests: 2,
      materials: [
        'Basic ruby chip',
        'Red flowers',
        'Simple copper items',
        'Red cloth',
        'Basic wheat offerings',
        'Traditional oil lamps'
      ],
      includes: [
        '2 qualified priests',
        'Basic Sun worship',
        'Simple Surya yantra',
        'Health and vitality blessings',
        'Basic prasadam'
      ],
      ritualSteps: [
        'Early morning Ganesh Puja',
        'Basic Surya invocation',
        'Essential Sun mantras',
        'Red flower offerings',
        'Vitality enhancement prayers'
      ],
      bestFor: ['Basic health issues', 'Low energy', 'Simple vitality boost', 'Father-son relationships'],
      // languages: ['Hindi', 'Sanskrit'],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
      whatThisFeelsLike: [
        ' A focused planetary balancing ritual addressing specific graha influences with disciplined mantra chanting.'
      ],
      jaapCount: '7000 Surya Mantra Jaap',
      highlightTags: ['Senior Acharya', 'All Pooja Samagri included', 'Havan included', 'Option to Participate in person at our site for Sankalp & Havan', 'Online Sankalp', 'Free pickup & Drop facility from your hotel/place in Varanasi to our site', 'Prashadam delivered to your home anywhere in World', 'Prasadam delivery times may vary based on location'],
    },
    {
      id: 'standard',
      name: 'Standard Surya Shanti',
      price: '₹18,000',
      originalPrice: '₹23,000',
      duration: '2-3 Days',
      features: [
        'Complete Sun worship',
        'Health and vitality boost',
        'Leadership enhancement',
        'Government job blessings',
        'Father relationship healing'
      ],
      popular: true,
      priests: 3,
      materials: [
        'Quality ruby',
        'Premium red flowers',
        'Traditional copper vessels',
        'Sacred red silk',
        'Pure wheat offerings',
        'Sun-specific herbs'
      ],
      includes: [
        '3 experienced priests',
        'Complete puja materials',
        'Energized Surya yantra',
        'Health enhancement rituals',
        'Leadership blessings',
        'Government job guidance'
      ],
      ritualSteps: [
        'Early dawn Ganesh Puja',
        'Complete Surya Kalash setup',
        'Full Sun mantra recitation',
        'Ruby consecration ceremony',
        'Health and leadership prayers',
        'Sunrise blessing aarti'
      ],
      bestFor: ['Health issues', 'Leadership roles', 'Government jobs', 'Father relationships', 'Authority positions'],
      // languages: ['Sanskrit', 'Hindi', 'English'],
      specialFeatures: [
        'Dawn time ceremony',
        'Ruby energizing',
        'Leadership enhancement mantras'
      ],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
      whatThisFeelsLike: [
        ' A stronger collective invocation with deeper mantra resonance and guidance.'
      ],
      jaapCount: '14,000 Surya Mantra Jaap',
      highlightTags: ['Senior Acharya', 'All Pooja Samagri included', 'Havan included', 'Option to Participate in person at our site for Sankalp & Havan', 'Online Sankalp', 'Free pickup & Drop facility from your hotel/place in Varanasi to our site', 'Prashadam delivered to your home anywhere in World', 'Prasadam delivery times may vary based on location']

    },
    {
      id: 'premium',
      name: 'Premium Surya Shanti',
      price: '₹32,000',
      originalPrice: '₹42,000',
      duration: '3-4 Days',
      features: [
        'Grand Sun ceremony',
        'Complete health transformation',
        'Supreme leadership qualities',
        'High authority positions',
        'Royal lifestyle blessings'
      ],
      priests: 4,
      materials: [
        'High-grade ruby',
        'Exotic red flowers',
        'Premium copper artifacts',
        'Golden red decorations',
        'Sacred wheat varieties',
        'Rare solar herbs'
      ],
      includes: [
        '4 master priests',
        'Grand sunrise ceremonial setup',
        'Premium ruby collection',
        'Complete health transformation',
        'Supreme leadership training',
        'Lifetime authority guidance'
      ],
      ritualSteps: [
        'Pre-dawn Grand Ganesh ceremony',
        'Elaborate Surya Kalash installation',
        'Extended solar power mantras',
        'Premium ruby consecration',
        'Complete health transformation',
        'Grand sunrise blessing ceremony'
      ],
      bestFor: ['Serious health issues', 'High leadership positions', 'Government authority', 'Royal status', 'Complete vitality'],
      // languages: ['Sanskrit', 'Hindi', 'English'],
      specialFeatures: [
        'Master priests with solar expertise',
        'Pre-dawn to sunrise ceremony',
        'Complete health transformation',
        'Supreme leadership guarantee'
      ],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
      whatThisFeelsLike: [
        ' A deeply immersive graha anushthan performed with sustained discipline and senior Acharya oversight.'
      ],
      jaapCount: '28,000 Surya Mantra Jaap',
      highlightTags: ['Senior Acharya', 'All Pooja Samagri included', 'Havan included', 'Option to Participate in person at our site for Sankalp & Havan', 'Online Sankalp', 'Free pickup & Drop facility from your hotel/place in Varanasi to our site', 'Prashadam delivered to your home anywhere in World', 'Prasadam delivery times may vary based on location']
    }
  ],
  'naam-karan-sanskar': [
    {
      id: 'basic',
      name: 'Basic Naam Karan',
      price: '₹8,000',
      originalPrice: '₹12,000',
      duration: '2 hours',
      features: [
        'Traditional naming ceremony',
        'Vedic name selection',
        'Basic ritual setup',
        'Family blessings'
      ],
      priests: 1,
      materials: [
        'Basic puja items',
        'Simple decorations',
        'Traditional sweets',
        'Basic flowers',
        'Simple name scroll',
        'Basic tilak materials'
      ],
      includes: [
        '1 trained Vedic pandit',
        'Naam Karan vidhi with mantra recitation',
        'Sankalp for the child’s health and well-being',
        'Required pooja samagri for Naam Karan',
        'Flowers and fruits for the ceremony',
        'Pandit ji dakshina and travel charges'
      ],
      ritualSteps: [
        'Ganesh Puja',
        'Family deity invocation',
        'Naam Karan vidhi',
        'Name announcement',
        'Aarti and blessings'
      ],
      bestFor: ['Parents of new born', 'Home-based ceremonies', 'Parents seeking a simple yet traditional ritual'],
      // languages: ['Hindi', 'Sanskrit'],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
       whatThisFeelsLike: [
        'A calm and sacred naming ceremony focused on blessings and correctness.'
      ],
      hidden: true

    },
    {
      id: 'standard',
      name: 'Traditional Naam Karan',
      price: '₹15,000',
      originalPrice: '₹20,000',
      duration: '2 hours',
      features: [
        'Complete naming ceremony',
        'Astrological name selection',
        'Traditional decorations',
        'Extended family rituals',
        'Photography coordination'
      ],
      popular: true,
      priests: 2,
      materials: [
        'Complete puja setup',
        'Traditional decorations',
        'Premium sweets',
        'Fresh flower arrangements',
        'Beautiful name scroll',
        'Sacred tilak varieties'
      ],
      includes: [
        '2 pandits Ji with Acharya supervision',
        'Guidance on muhurat and ritual sequence',
        'Complete pooja samagri for Naam Karan',
        'Flowers and fruits for the ceremony',
        'Pandit ji dakshina and travel charges'
      ],
      ritualSteps: [
        'Ganesh Poojan',
        'Detailed Sankalp',
        'Naam Karan vidhi',
        'Aarti and blessings',
        'Blessing rituals'
      ],
      bestFor: ['Parents of a new born', 'Astrological naming', 'Those seeking guidance and ritual confidence'],
      // languages: ['Sanskrit', 'Hindi', 'English'],
      specialFeatures: [
        'Astrological name selection',
        'Beautiful ceremony setup',
        'Family photography coordination'
      ],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
         whatThisFeelsLike: [
        'A guided ceremony with greater family involvement and reassurance for performing the pooja correctly.'
      ], 
      jaapCount: 'Naam karan as per Vedic shastra',
      highlightTags: ['Senior Acharya', 'All Pooja Samagri included', 'Aashirvachan', 'Travel(to & from Varansi), stay, dakhsina for Acharya(s) ji included', 'Prayer for well being of the child'],

    },
    {
      id: 'premium',
      name: 'Grand Naam Karan',
      price: '₹25,000',
      originalPrice: '₹35,000',
      duration: '4 hours',
      features: [
        'Grand naming ceremony',
        'Multiple astrological consultations',
        'Luxury decorations',
        'Extended celebrations',
        'Professional documentation'
      ],
      priests: 3,
      materials: [
        'Luxury puja setup',
        'Grand decorations',
        'Premium sweet varieties',
        'Exotic flower arrangements',
        'Artistic name scroll',
        'Premium tilak collection'
      ],
      includes: [
        '3 pandits led by a Senior Acharya',
        'Detailed, family-specific sankalp',
        'Pre- and post-ceremony guidance',
        'Full ritual setup and samagri for a grand ceremony',
        'Flowers and fruits for the ceremony',
        'All Pandit ji dakshina and travel charges'
      ],
      ritualSteps: [
        'Ganesh Poojan',
        'Detailed Sankalp',
        'Naam Karan vidhi',
        'Aarti & Ashirvachan'
      ],
      bestFor: ['Parents of a newborn', 'Families prioritising complete traditional observance', 'Ceremonies where ritual depth matters'],
      // languages: ['Sanskrit', 'Hindi', 'English'],
      specialFeatures: [
        'Multiple astrology consultations',
        'Professional ceremony documentation',
        'Grand celebration setup',
        'Lifetime name significance guide'
      ],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
       whatThisFeelsLike: [
        'A fully anchored sanskar with traditional depth and clarity.'
      ],
      hidden: true


    }
  ],
  'annaprashan-sanskar': [
    {
      id: 'basic',
      name: 'Basic Annaprashan',
      price: '₹10,000',
      originalPrice: '₹14,000',
      duration: '2 hours',
      features: [
        'Traditional first feeding',
        'Basic ritual setup',
        'Simple food blessing',
        'Family participation'
      ],
      priests: 1,
      materials: [
        'Basic puja items',
        'Simple food preparations',
        'Traditional kheer',
        'Basic decorations',
        'Simple feeding utensils',
        'Basic tilak materials'
      ],
      includes: [
        '1 trained pandit',
        'Annaprashan vidhi with mantra',
        'Sankalp for nourishment and health',
        'Pooja samagri and basic havan setup (if advised)',
        'Flowers and fruits for the ceremony',
        'Pandit dakshina and travel charges'
      ],
      ritualSteps: [
        'Ganesh Poojan',
        'Sankalp for the child\'s health and nourishment',
        'Annaprashan vidhi',
        'Aarti and blessings'
      ],
      bestFor: ['Small family ceremonies', 'Parents seeking a meaningful yet simple observance'],
      // languages: ['Hindi', 'Sanskrit'],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
       whatThisFeelsLike: [
        'A warm and celebratory ritual marking a key milestone.'
      ],
      hidden: true

    },
    {
      id: 'standard',
      name: 'Traditional Annaprashan',
      price: '₹18,000',
      originalPrice: '₹24,000',
      duration: '3-4 hours',
      features: [
        'Complete first feeding ceremony',
        'Traditional food preparations',
        'Child health blessings',
        'Growth and prosperity rituals',
        'Photography coordination'
      ],
      popular: true,
      priests: 2,
      materials: [
        'Complete puja setup',
        'Traditional food varieties',
        'Premium kheer preparations',
        'Beautiful decorations',
        'Sacred feeding utensils',
        'Child-specific tilak'
      ],
      includes: [
        '2 pandits with Acharya supervision',
        'Expanded sankalp including family blessings',
        'Guidance on food offering and ritual order',
        'Complete pooja samagri and havan setup',
        'Flowers and fruits for the ceremony',
        'Pandit ji dakshina and travel charges'
      ],
      ritualSteps: [
        'Ganesh Poojan',
        'Detailed Sankalp for the child\'s health, growth, and prosperity',
        ' Annaprashan vidhi with traditional food offerings',
        'Aarti and blessings'
      ],
      bestFor: ['Families involving elders', 'Those seeking clarity and structure', 'Child health focus'],
      // languages: ['Sanskrit', 'Hindi', 'English'],
      specialFeatures: [
        'Traditional food preparations',
        'Child health blessings',
        'Growth prosperity rituals'
      ],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
      whatThisFeelsLike: [
        ' A guided ceremony with added structure and continuity.'
      ], 
      jaapCount: 'Annaprashan as per Vedic shastra',
      highlightTags: ['Senior Acharya', 'All Pooja Samagri included', 'Aashirvachan', 'Travel(to & from Varansi), stay, dakhsina for Acharya(s) ji included', 'Prayer for well being of the child'],
    },
    {
      id: 'premium',
      name: 'Grand Annaprashan',
      price: '₹30,000',
      originalPrice: '₹42,000',
      duration: '5 hours',
      features: [
        'Grand first feeding ceremony',
        'Multiple food blessings',
        'Extended child rituals',
        'Luxury celebrations',
        'Complete documentation'
      ],
      priests: 3,
      materials: [
        'Luxury ceremony setup',
        'Premium food varieties',
        'Multiple kheer types',
        'Grand decorations',
        'Silver feeding utensils',
        'Premium child care items'
      ],
      includes: [
        '4 pandits led by Senior Acharya',
        'Detailed sankalp and blessings for the child\'s health, growth, prosperity, and family well-being',
        'Pre-ceremony guidance',
        'Full ritual setup and materials',
        'Pandit ji dakshina and travel charges'
      ],
      ritualSteps: [
        'Ganesh Poojan',
        'Detailed Sankalp',
        'Traditional first feeding ceremony',
        'Annaprashan vidhi with multiple food offerings',
        'Aarti & Ashirvachan'
      ],
      bestFor: ['Formal family ceremonies', 'Traditional observance with extended families', 'Complete child blessings'],
      // languages: ['Sanskrit', 'Hindi', 'English'],
      specialFeatures: [
        'Multiple food blessing ceremonies',
        'Extended child health rituals',
        'Professional documentation',
        'Silver feeding utensil blessing'
      ],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
      whatThisFeelsLike: [
        'A complete Annaprashan sanskar with traditional grounding.'
      ],
      hidden: true
    }
  ],
  'upnayan-sanskar': [
    {
      id: 'basic',
      name: 'Basic Upnayan',
      price: '₹20,000',
      originalPrice: '₹28,000',
      duration: '1-2 Days',
      features: [
        'Sacred thread ceremony',
        'Basic Vedic initiation',
        'Simple Gayatri mantra teaching',
        'Traditional setup'
      ],
      priests: 2,
      materials: [
        'Sacred thread (yajnopavit)',
        'Basic ceremony items',
        'Simple decorations',
        'Traditional clothes',
        'Basic learning materials',
        'Sacred fire setup'
      ],
      includes: [
        '1 trained pandit',
        'Upanayan vidhi with mantra recitation',
        'Havan setup and all required pooja samagri for Upnayan',
        'Flowers and fruits for the ceremony',
        'Pandit ji dakshina and travel charges',
        'Prasadam'
      ],
      ritualSteps: [
        'Ganesh Puja',
        'Upanayan vidhi with sacred thread installation',
        'Gayatri updesh',
        'Aarti and blessings',
        'Sankalp'
      ],
      bestFor: ['Families preferring a concise ceremony', 'Budget-friendly option', 'Simple, focused observance'],
      // languages: ['Hindi', 'Sanskrit'],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
      whatThisFeelsLike: [
        'A focused initiation performed with correctness'
      ],
      jaapCount: 'Upnayan as per Vedic shastra',
      highlightTags: ['Senior Acharya Supervision', 'Gayatri updesh', 'All Pooja Samagri included', 'Havan included', 'Travel(to & from Varansi), stay, dakhsina for Acharya(s) ji included',],
    },
    {
      id: 'standard',
      name: 'Traditional Upnayan',
      price: '₹35,000',
      originalPrice: '₹45,000',
      duration: '1-2 Days',
      features: [
        'Complete sacred thread ceremony',
        'Full Vedic initiation',
        'Comprehensive mantra teaching',
        'Traditional guru-disciple setup',
        'Extended learning session'
      ],
      popular: true,
      priests: 3,
      materials: [
        'Premium sacred threads',
        'Complete ceremony setup',
        'Traditional decorations',
        'Sacred learning materials',
        'Premium clothes',
        'Extended fire ceremony items'
      ],
      includes: [
        '2 pandits with Acharya supervision',
        ' Sankalp and guidance on ritual sequence',
        'Guidance on post-Upanayan practices',
        'Guidance on post-Upanayan practices',
        'All Pandit ji dakshina and travel charges',
        'Prasadam'
      ],
      ritualSteps: [
        'Ganesh Poojan',
        'Detailed Sankalp',
        'Upanayan vidhi with sacred thread installation',
        'Gayatri updesh',
        'Aarti and blessings',
        'Blessing ceremonies'
      ],
      bestFor: ['Families seeking clarity beyond the ceremony', 'First-time Upanayan observance'],
      // languages: ['Sanskrit', 'Hindi', 'English'],
      specialFeatures: [
        'Guru-disciple relationship setup',
        'Comprehensive Vedic teaching',
        'Extended learning sessions'
      ],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
      whatThisFeelsLike: [
        'A guided initiation with Acharya oversight.'
      ],
      jaapCount: 'Upnayan as per Vedic shastra',
      highlightTags: ['Senior Acharya Supervision', 'Gayatri updesh', 'Haldi Ceremony','All Pooja Samagri included', 'Havan included', 'Travel(to & from Varansi), stay, dakhsina for Acharya(s) ji included',],
    
    },
    {
      id: 'premium',
      name: 'Grand Upnayan',
      price: '₹55,000',
      originalPrice: '₹75,000',
      duration: '1-2 Days',
      features: [
        'Grand sacred thread ceremony',
        'Complete Brahmin initiation',
        'Extended Vedic education',
        'Multiple guru teachings',
        'Grand celebrations'
      ],
      priests: 4,
      materials: [
        'Gold-blessed sacred threads',
        'Grand ceremony setup',
        'Luxury decorations',
        'Complete Vedic materials',
        'Premium traditional clothes',
        'Grand fire ceremony setup'
      ],
      includes: [
        '4 pandits led by Senior Acharya',
        'Detailed sankalp and instruction',
        'Pre- and post-ceremony guidance',
        'Full ritual execution with all samagri for a grand ceremony',
        'All Pandit ji dakshina and travel charges'
      ],
      ritualSteps: [
        'Ganesh Puja ceremony',
        'Detailed Sankalp and instruction',
        'Upanayan vidhi',
        'Gayatri updesh',
        'Aarti & Ashirvachan'
      ],
      bestFor: ['Families committed to traditional discipline', 'Full ceremonial observance'],
      // languages: ['Sanskrit', 'Hindi', 'English'],
      specialFeatures: [
        'Multiple guru teachings',
        'Complete Brahmin initiation',
        'Extended Vedic education',
        'Lifetime spiritual guidance'

      ],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
      whatThisFeelsLike: [
        'A complete Upanayan sanskar with depth and continuity.'
      ],
      jaapCount: 'Upnayan as per Vedic shastra',
      highlightTags: ['Senior Acharya Supervision', 'Gayatri updesh', 'Haldi Ceremony','All Pooja Samagri included', 'Havan included', 'Travel(to & from Varansi), stay, dakhsina for Acharya(s) ji included',],
    
    }
  ],
  'shubh-vivah': [
    {
      id: 'basic',
      name: 'Basic Vivah',
      price: '₹35,000',
      originalPrice: '₹50,000',
      duration: '5* hours',
      features: [
        'Essential wedding rituals',
        'Basic Vedic ceremonies',
        'Simple mandap setup',
        'Core marriage rituals'
      ],
      priests: 2,
      materials: [
        'Basic mandap setup',
        'Essential ceremony items',
        'Simple decorations',
        'Traditional fire setup',
        'Basic flower arrangements',
        'Essential wedding materials'
      ],
      includes: [
        '2 trained pandits',
        'Core Vivaah vidhi: Ganesh Poojan, Kanyadaan, Vivaha Homa, Saptapadi',
        'All required poojan samagri and havan setup (please note that any family or tradition specific poojan items to be arranged by the devotees, we will arrange for all generic poojan samagri for Vivaah)',
        'Pandit Ji dakshina and travel charges'
      ],
      ritualSteps: [
        'Ganesh Puja',
        'Basic mandap setup',
        'Essential wedding rituals',
        'Kanyadaan and Vivaha Homa',
        'Marriage blessing ceremony'
      ],
      bestFor: ['Small, intimate weddings', 'Budget-friendly option', 'Families experienced with wedding coordination'],
      // languages: ['Hindi', 'Sanskrit'],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
      whatThisFeelsLike: [
        'A concise yet complete Vedic marriage ceremony performed with correctness and devotion.'
      ],
      // jaapCount: '51,000 Jaap',
      highlightTags: ['Acharya ji from Kashi', 'Complete Vedic Vivah Vidhi',  'Dwar Char','All Pujan Samagri Included', 'Dakshina & Travel of Kashi Acharya included'],
      
    },
    {
      id: 'standard',
      name: 'Traditional Vivah',
      price: '₹75,000',
      originalPrice: '₹1,00,000',
      duration: '6 hours',
      features: [
        'Complete traditional wedding',
        'Full Vedic ceremonies',
        'Beautiful mandap setup',
        'Extended rituals',
        'Family participation ceremonies'
      ],
      popular: true,
      priests: 3,
      materials: [
        'Traditional mandap setup',
        'Complete ceremony decorations',
        'Premium flower arrangements',
        'Sacred wedding fire setup',
        'Traditional wedding clothes',
        'Complete ritual materials'
      ],
      includes: [
        '3 pandits with Acharya supervision',
        'Expanded sankalp',
        'Pre-ceremony guidance on ritual sequence',
        'All required poojan samagri and havan setup (please note that any family or tradition specific poojan items to be arranged by the devotees, we will arrange for all generic poojan samagri for Vivaah)',
        'All Pandit ji dakshina and travel charges'

      ],
      ritualSteps: [
        'Ganesh Poojan',
        'Detailed Sankalp',
        'Full Vedic wedding rituals',
        'Kanyadaan and Vivaha Homa',
        'Complete blessing rituals'
      ],
      bestFor: ['Medium-sized weddings', 'Families seeking reassurance and structure','Cultural weddings'],
      // languages: ['Sanskrit', 'Hindi', 'English'],
      specialFeatures: [
        'Complete traditional setup',
        'Extended family ceremonies',
        'Beautiful mandap decoration'
      ],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
      whatThisFeelsLike: [
        'A well-guided ceremony with smoother coordination.'
      ],
      // jaapCount: '51,000 Jaap',
      planTag: 'Family Favorite',
      highlightTags: ['1 Senior Kashi Acharya', 'Complete Vedic Vivah Vidhi', 'Dwar Char', 'All Poojan Samagri Included', 'Dakshina & Travel of Kashi Acharya included'],
      
    },
    {
      id: 'premium',
      name: 'Grand Royal Vivah',
      price: '₹1,50,000',
      originalPrice: '₹2,00,000',
      duration: '6-7* hours',
      features: [
        'Royal wedding ceremony',
        'Complete multi-day rituals',
        'Luxury mandap setup',
        'Extended celebrations',
        'Professional coordination'
      ],
      priests: 5,
      materials: [
        'Royal mandap setup',
        'Luxury decorations',
        'Premium flower arrangements',
        'Grand fire ceremony setup',
        'Royal wedding attire',
        'Complete luxury materials'
      ],
      includes: [
        '4 trained pandits led by a Senior Acharya',
        'Detailed, purpose-specific sankalp',
        'Full traditional pacing of rituals',
        'Comprehensive pre-ceremony guidance',
        'Complete ceremonial setup',
        'Pandit dakshina and travel charges'
      ],
      ritualSteps: [
        'Ganesh Poojan',
        'Detailed Sankalp',
        'Kanyadaan and Vivaha Homa',
        'Saptapadi and complete wedding rituals',
        'Ashirvachan and blessings'
      ],
      bestFor: ['Large weddings with huge gatherings', 'Families seeking full traditional depth without confusion'],
      // languages: ['Sanskrit', 'Hindi', 'English'],
      specialFeatures: [
        'Royal ceremony setup',
        'Multi-day celebrations',
        'Professional coordination',
        'Luxury wedding experience'
      ],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
      whatThisFeelsLike: [
        'A complete Vedic Vivaah conducted with depth and calm assurance.'
      ],
      planTag: 'Royal & Divine Experience',
      jaapCount: 'Special 15 min Vedic Aashirvaad to the couple at Jai Maala',
      highlightTags: ['2 Senior Kashi Acharya', 'Pre consultation with senior Acharya Ji','Complete Vedic Vivah Vidhi','Comprehensive Dwar Char', 'All Poojan Samagri Included', 'Dakshina & Travel of Kashi Acharya included'],
      
    }
  ],
  'grih-pravesh-pujan': [
    {
      id: 'basic',
      name: 'Basic Grih Pravesh',
      price: '₹12,000',
      originalPrice: '₹16,000',
      duration: '3 hours',
      features: [
        'Essential home blessing',
        'Basic Vastu purification',
        'Simple entry rituals',
        'Prosperity blessings'
      ],
      priests: 2,
      // materials: [
      //   'Basic Kalash setup',
      //   'Simple decorations',
      //   'Traditional lamps',
      //   'Basic flowers',
      //   'Essential puja items',
      //   'Simple rangoli materials'
      // ],
      includes: [
        '2 qualified priests',
        'Basic home blessing ceremony',
        'Essential Vastu rituals',
        'Simple entry purification',
        'Prosperity prayers',
        'Basic prasadam'
      ],
      ritualSteps: [
        'Ganesh Puja at entrance',
        'Basic Vastu purification',
        'Home blessing ceremony',
        'First entry ritual',
        'Prosperity prayers'
      ],
      bestFor: ['New home blessing', 'Simple ceremonies', 'Budget-friendly option'],
      // languages: ['Hindi', 'Sanskrit'],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
      whatThisFeelsLike: [
        'A sincere and focused ritual performed with correctness, discipline, and devotion. Ideal for those seeking to perform the pooja properly without complexity.'
      ],
      jaapCount: 'Shanti Paath for Peace',
      highlightTags: ['1 Senior Acharya', 'Brass Ganesh Laxmi Murti Complimentary', 'All Poojan Samagri Included', 'Havan Included', 'Travel(to & from Varansi), stay, dakhsina for Acharya(s) ji included'],
      
    },
    {
      id: 'standard',
      name: 'Traditional Grih Pravesh',
      price: '₹22,000',
      originalPrice: '₹30,000',
      duration: '4-5 hours',
      features: [
        'Complete home blessing',
        'Full Vastu consultation',
        'Traditional entry ceremony',
        'Prosperity enhancement',
        'Room-wise purification'
      ],
      popular: true,
      priests: 3,
      // materials: [
      //   'Complete Kalash arrangements',
      //   'Traditional decorations',
      //   'Premium flower arrangements',
      //   'Sacred lamps collection',
      //   'Complete puja setup',
      //   'Beautiful rangoli designs'
      // ],
      includes: [
        '3 experienced priests',
        'Complete home blessing',
        'Full Vastu consultation',
        'Room-wise purification',
        'Traditional entry ceremony',
        'Prosperity feast'
      ],
      ritualSteps: [
        'Elaborate Ganesh Puja',
        'Complete Vastu purification',
        'Room-wise blessing ceremony',
        'Traditional first entry',
        'Prosperity enhancement rituals',
        'Home harmony prayers'
      ],
      bestFor: ['Traditional families', 'Complete home blessing', 'Vastu compliance'],
      // languages: ['Sanskrit', 'Hindi', 'English'],
      specialFeatures: [
        'Complete Vastu consultation',
        'Room-wise purification',
        'Traditional entry ceremonies'
      ],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
      whatThisFeelsLike: [
        'A deeper and more immersive ritual experience with enhanced collective energy, guidance, and continuity..'
      ],
      jaapCount: 'Shanti & Shri Sukt Paath',
      highlightTags: ['1 Senior Acharya', 'Brass Ganesh Laxmi Murti Complimentary', 'All Poojan Samagri Included', 'Shree Yantra Frame Complimentary', 'Havan and Prayer for prosperity', 'Travel(to & from Varansi), stay, dakhsina for Acharya(s) ji included'],
      
    },
    {
      id: 'premium',
      name: 'Grand Grih Pravesh',
      price: '₹45,000',
      originalPrice: '₹60,000',
      duration: '6-8 hours',
      features: [
        'Grand home blessing ceremony',
        'Complete Vastu transformation',
        'Luxury decorations',
        'Extended prosperity rituals',
        'Professional coordination'
      ],
      priests: 4,
      materials: [
        'Grand Kalash installations',
        'Luxury home decorations',
        'Premium flower arrangements',
        'Sacred lighting setup',
        'Complete ceremony materials',
        'Artistic rangoli designs'
      ],
      includes: [
        '4 master priests',
        'Grand home blessing ceremony',
        'Complete Vastu transformation',
        'Extended prosperity rituals',
        'Professional ceremony coordination',
        'Grand celebration feast'
      ],
      ritualSteps: [
        'Grand entrance Ganesh ceremony',
        'Complete Vastu transformation',
        'Grand room blessing ceremonies',
        'Traditional royal entry',
        'Extended prosperity rituals',
        'Grand home harmony celebrations'
      ],
      bestFor: ['Luxury homes', 'Grand celebrations', 'Complete transformation'],
      // languages: ['Sanskrit', 'Hindi', 'English'],
      specialFeatures: [
        'Complete Vastu transformation',
        'Professional coordination',
        'Grand ceremony setup',
        'Lifetime home harmony guidance'
      ],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
      whatThisFeelsLike: [
        'An anushthan-style observance conducted with depth, discipline, and sustained spiritual focus, led by senior Acharyas.'
      ],
      jaapCount: 'Shanti & Shri Sukt Paath',
      highlightTags: ['2 Senior Acharya', 'Brass Ganesh Laxmi Murti Complimentary', 'All Poojan Samagri Included', 'Ashphatic Shree Yantra', 'Havan and Prayer for prosperity', 'Raksha kavach for the home', 'Travel(to & from Varansi), stay, dakhsina for Acharya(s) ji included'],
      
    }
  ],
  'satyanarayan-pujan': [
    {
      id: 'basic',
      name: 'Basic Satyanarayan Pujan',
      price: '₹8,000',
      originalPrice: '₹12,000',
      duration: '2.5 hours',
      features: [
        'Essential Satyanarayan worship',
        'Basic story recitation',
        'Simple offerings',
        'Family blessings'
      ],
      priests: 1,
      materials: [
        'Basic puja items',
        'Simple banana arrangements',
        'Traditional panchamrit',
        'Basic flowers',
        'Essential story book',
        'Simple prasadam ingredients'
      ],
      includes: [
        '1 qualified priest',
        'Essential Satyanarayan worship',
        'Basic story recitation',
        'Simple blessing ceremony',
        'Family prayer session',
        'Traditional prasadam'
      ],
      ritualSteps: [
        'Ganesh Puja',
        'Satyanarayan invocation',
        'Essential story recitation',
        'Simple offerings',
        'Family blessing prayers'
      ],
      bestFor: ['Regular monthly pujas', 'Simple family ceremonies', 'Budget-friendly option'],
      // languages: ['Hindi', 'Sanskrit'],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
      whatThisFeelsLike: [
        'A sincere and focused ritual performed with correctness, discipline, and devotion. Ideal for those seeking to perform the pooja properly without complexity.'
      ],
      // jaapCount: 'Special 15 min Vedic Aashirvaad to the couple at Jai Maala',
      highlightTags: ['Ganesh Poojan', 'All Poojan Samagri Included', 'Havan included', 'Travel(to & from Varansi), stay & dakhsina for Acharya(s) ji included'],
      
    },
    {
      id: 'standard',
      name: 'Traditional Satyanarayan Pujan',
      price: '₹15,000',
      originalPrice: '₹20,000',
      duration: '3-4 hours',
      features: [
        'Complete Satyanarayan worship',
        'Full story recitation',
        'Traditional offerings',
        'Extended family participation',
        'Prosperity enhancement'
      ],
      popular: true,
      priests: 2,
      materials: [
        'Complete puja setup',
        'Premium banana offerings',
        'Traditional panchamrit varieties',
        'Fresh flower arrangements',
        'Complete story recitation',
        'Premium prasadam ingredients'
      ],
      includes: [
        '2 experienced priests',
        'Complete Satyanarayan worship',
        'Full story recitation',
        'Extended family ceremonies',
        'Prosperity blessing rituals',
        'Traditional feast preparation'
      ],
      ritualSteps: [
        'Elaborate Ganesh Puja',
        'Complete Satyanarayan setup',
        'Full story recitation with explanations',
        'Traditional offering ceremonies',
        'Extended family blessing rituals',
        'Prosperity enhancement prayers'
      ],
      bestFor: ['Traditional families', 'Complete ceremonies', 'Prosperity focus'],
      // languages: ['Sanskrit', 'Hindi', 'English'],
      specialFeatures: [
        'Complete story recitation',
        'Extended family participation',
        'Prosperity enhancement rituals'
      ],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
      whatThisFeelsLike: [
        'A deeper and more immersive ritual experience with enhanced collective energy, guidance, and continuity..'
      ],
       // jaapCount: 'Special 15 min Vedic Aashirvaad to the couple at Jai Maala',
      highlightTags: ['1 Senior Acharya', 'Ganesh poojan', 'All Poojan Samagri Included', 'Havan included', 'Travel(to & from Varansi), stay & dakhsina for Acharya(s) ji included'],
      
    },
    {
      id: 'premium',
      name: 'Grand Satyanarayan Pujan',
      price: '₹28,000',
      originalPrice: '₹38,000',
      duration: '5 hours',
      features: [
        'Grand Satyanarayan ceremony',
        'Extended story sessions',
        'Luxury offerings',
        'Community participation',
        'Large-scale celebrations'
      ],
      priests: 3,
      materials: [
        'Grand puja setup',
        'Luxury banana arrangements',
        'Premium panchamrit varieties',
        'Exotic flower decorations',
        'Multiple story sessions',
        'Grand prasadam arrangements'
      ],
      includes: [
        '3 master priests',
        'Grand Satyanarayan ceremony',
        'Extended story sessions',
        'Community blessing rituals',
        'Large-scale celebrations',
        'Grand community feast'
      ],
      ritualSteps: [
        'Grand Ganesh Puja ceremony',
        'Elaborate Satyanarayan installation',
        'Extended story recitation sessions',
        'Grand offering ceremonies',
        'Community blessing rituals',
        'Grand celebration feast'
      ],
      bestFor: ['Community celebrations', 'Large gatherings', 'Grand ceremonies'],
      // languages: ['Sanskrit', 'Hindi', 'English'],
      specialFeatures: [
        'Extended story sessions',
        'Community participation',
        'Grand celebration setup',
        'Large-scale feast arrangements'
      ],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
      whatThisFeelsLike: [
        'An anushthan-style observance conducted with depth, discipline, and sustained spiritual focus, led by senior Acharyas.'
      ],
      jaapCount: 'Vishnu Sahastranam Paath',
      planTag: 'Family Choice',
      highlightTags: ['1 Senior Acharya', 'Ganesh Poojan','All Poojan Samagri Included', 'Tulsi Mamla for devotee', 'Havan included', 'Travel(to & from Varansi), stay & dakhsina for Acharya(s) ji included'],
      
    }
  ],
  'shanti-path': [
    {
      id: 'basic',
      name: 'Basic Shanti Path',
      price: '₹10,000',
      originalPrice: '₹14,000',
      duration: '2 hours',
      features: [
        'Essential peace mantras',
        'Basic Vedic chanting',
        'Simple purification',
        'Family harmony blessings'
      ],
      priests: 2,
      materials: [
        'Basic puja items',
        'Simple sacred fire setup',
        'Traditional herbs',
        'Basic flowers',
        'Essential mantras books',
        'Simple ahuti materials'
      ],
      includes: [
        '2 qualified priests',
        'Essential peace mantras',
        'Basic Vedic chanting',
        'Simple purification rituals',
        'Family harmony prayers',
        'Basic prasadam'
      ],
      ritualSteps: [
        'Ganesh Puja',
        'Sacred fire preparation',
        'Essential peace mantras',
        'Basic purification ceremony',
        'Family harmony blessings'
      ],
      bestFor: ['Regular peace rituals', 'Family harmony', 'Simple ceremonies'],
      // languages: ['Hindi', 'Sanskrit'],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
      whatThisFeelsLike: [
        'A sincere and focused ritual performed with correctness, discipline, and devotion. Ideal for those seeking to perform the pooja properly without complexity.'
      ],
      jaapCount: 'Shanti Paath for Peace',
      highlightTags: ['1 Senior Acharya',  'All Poojan Samagri Included', 'Travel(to & from Varansi), stay, dakhsina for Acharya(s) ji included'],
      
    },
    {
      id: 'standard',
      name: 'Traditional Shanti Path',
      price: '₹18,000',
      originalPrice: '₹25,000',
      duration: '3-4 hours',
      features: [
        'Complete peace ceremony',
        'Full Vedic recitations',
        'Extended purification',
        'Obstacle removal',
        'Comprehensive harmony rituals'
      ],
      popular: true,
      priests: 3,
      materials: [
        'Complete havan setup',
        'Premium sacred fire materials',
        'Traditional herb collections',
        'Fresh flower arrangements',
        'Complete mantra collections',
        'Premium ahuti ingredients'
      ],
      includes: [
        '3 experienced priests',
        'Complete peace ceremony',
        'Full Vedic recitations',
        'Extended purification rituals',
        'Obstacle removal ceremonies',
        'Traditional feast'
      ],
      ritualSteps: [
        'Elaborate Ganesh Puja',
        'Complete havan setup',
        'Full Vedic peace recitations',
        'Extended purification ceremonies',
        'Obstacle removal rituals',
        'Comprehensive harmony blessings'
      ],
      bestFor: ['Complete peace rituals', 'Obstacle removal', 'Family problems resolution'],
      // languages: ['Sanskrit', 'Hindi', 'English'],
      specialFeatures: [
        'Complete Vedic recitations',
        'Extended purification rituals',
        'Obstacle removal ceremonies'
      ],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
      whatThisFeelsLike: [
        'A deeper and more immersive ritual experience with enhanced collective energy, guidance, and continuity..'
      ],
      jaapCount: 'Ganesharachana with Shanti Paath',
      highlightTags: ['1 Senior Acharya', 'Brass Ganesh Laxmi Murti Complimentary', 'All Poojan Samagri Included', 'Travel(to & from Varansi), stay, dakhsina for Acharya(s) ji included'],
      
    },
    {
      id: 'premium',
      name: 'Grand Shanti Path',
      price: '₹35,000',
      originalPrice: '₹48,000',
      duration: '6 hours',
      features: [
        'Grand peace ceremony',
        'Extended Vedic sessions',
        'Complete purification',
        'Major obstacle removal',
        'Comprehensive protection'
      ],
      priests: 5,
      materials: [
        'Grand havan setup',
        'Luxury sacred fire arrangements',
        'Premium herb collections',
        'Exotic flower decorations',
        'Extended mantra sessions',
        'Grand ahuti ceremonies'
      ],
      includes: [
        '5 master priests',
        'Grand peace ceremony',
        'Extended Vedic sessions',
        'Complete purification rituals',
        'Major obstacle removal',
        'Grand protection ceremonies'
      ],
      ritualSteps: [
        'Grand Ganesh Puja ceremony',
        'Elaborate havan installation',
        'Extended Vedic peace sessions',
        'Complete purification ceremonies',
        'Major obstacle removal rituals',
        'Grand protection and harmony ceremonies'
      ],
      bestFor: ['Major life problems', 'Complete purification', 'Grand peace ceremonies'],
      // languages: ['Sanskrit', 'Hindi', 'English'],
      specialFeatures: [
        'Extended Vedic sessions',
        'Complete purification ceremonies',
        'Major obstacle removal',
        'Comprehensive protection rituals'
      ],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
      whatThisFeelsLike: [
        'An anushthan-style observance conducted with depth, discipline, and sustained spiritual focus, led by senior Acharyas.'
      ],
      jaapCount: 'Ganesharachana with Shanti Paath',
      highlightTags: ['1 Senior Acharya', 'Brass Ganesh Laxmi Murti Complimentary', 'All Poojan Samagri Included', 'Travel(to & from Varansi), stay, dakhsina for Acharya(s) ji included'],
      
    }
  ],
  'manglik-dosh-nivaran': [
    {
      id: 'basic',
      name: 'Basic Manglik Dosh Nivaran',
      price: '₹15,000',
      originalPrice: '₹20,000',
      duration: '3 hours',
      features: [
        'Essential Manglik remedies',
        'Basic Mars pacification',
        'Simple coral blessings',
        'Marriage obstacle removal'
      ],
      priests: 2,
      materials: [
        'Basic red coral',
        'Red flowers and cloth',
        'Traditional copper items',
        'Simple jaggery offerings',
        'Basic Mars yantra',
        'Essential remedial items'
      ],
      includes: [
        '2 qualified priests',
        'Basic Manglik assessment',
        'Essential Mars pacification',
        'Simple coral energizing',
        'Marriage blessing rituals',
        'Basic remedial guidance'
      ],
      ritualSteps: [
        'Ganesh Puja',
        'Mars deity invocation',
        'Basic Manglik remedies',
        'Coral consecration',
        'Marriage blessing ceremony'
      ],
      bestFor: ['Mild Manglik dosha', 'Basic marriage delays', 'Simple remedies'],
      // languages: ['Hindi', 'Sanskrit'],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
      whatThisFeelsLike: [
        'A sincere and focused ritual performed with correctness, discipline, and devotion. Ideal for those seeking to perform the pooja properly without complexity.'
      ],
      hidden: true,
      jaapCount: 'Mangal Jaap',
      highlightTags: ['Senior Acharya', 'All Pooja Samagri included', 'Havan will be performed'],
    },
    {
      id: 'standard',
      name: 'Complete Manglik Dosh Nivaran',
      price: '₹25,000',
      originalPrice: '₹35,000',
      duration: '4-5 hours',
      features: [
        'Complete Manglik analysis',
        'Full Mars pacification',
        'Premium coral energizing',
        'Extended marriage rituals',
        'Comprehensive remedies'
      ],
      popular: true,
      priests: 3,
      // materials: [
      //   'Premium red coral',
      //   'Fresh red flowers',
      //   'Sacred copper vessels',
      //   'Pure jaggery varieties',
      //   'Energized Mars yantra',
      //   'Complete remedial kit'
      // ],
      includes: [
        '3 experienced priests',
        'Full Mars pacification ceremony',
        'Kashi Acharyas travel, stay and dakshina included',
        'All Puja Samagri included',
        'Comprehensive remedial guidance'
      ],
      ritualSteps: [
        'Elaborate Mars pacification',
        'Complete Manglik remedies',
        'Comprehensive blessing rituals'
      ],
      bestFor: ['Moderate to severe Manglik dosha', 'Marriage delays', 'Relationship problems'],
      // languages: ['Sanskrit', 'Hindi', 'English'],
      specialFeatures: [
        'Complete horoscope analysis',
        'Premium coral energizing',
        'Extended marriage rituals'
      ],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
      whatThisFeelsLike: [
        'A deeper and more immersive ritual experience with enhanced collective energy, guidance, and continuity..'
      ],
      jaapCount: 'Mangal Shanti Jaap',
      highlightTags: ['Pre Consultation with Senior Acharya for problem analysis', '1 Senior Acharya', 'Havan will be performed', '108 Mangal Beej Mantra Jaap', 'All Pooja Samagri included',],
      siteHighlightTags: ['Pre Consultation with Senior Acharya for problem analysis', '1 Senior Acharya', 'Kumbh Vivah (if required)', '108 Mangal Beej Mantra Jaap', 'All Pooja Samagri included',],
    },
    {
      id: 'premium',
      name: 'Advanced Manglik Dosh Nivaran',
      price: '₹45,000',
      originalPrice: '₹60,000',
      duration: '6-7 hours',
      features: [
        'Advanced Manglik remedies',
        'Complete Mars transformation',
        'High-grade coral rituals',
        'Guaranteed marriage solutions',
        'Lifetime remedial support'
      ],
      priests: 4,
      materials: [
        'High-grade red coral',
        'Exotic red flowers',
        'Premium copper artifacts',
        'Sacred jaggery varieties',
        'Multiple Mars yantras',
        'Advanced remedial materials'
      ],
      includes: [
        '4 master astrologer-priests',
        'Advanced astrological analysis',
        'Complete Mars transformation',
        'High-grade coral consecration',
        'Guaranteed marriage solutions',
        'Lifetime remedial support'
      ],
      ritualSteps: [
        'Advanced astrological assessment',
        'Complete Mars transformation',
        'Advanced Manglik remedies',
        'High-grade coral energizing',
        'Guaranteed marriage ceremonies',
        'Lifetime blessing rituals'
      ],
      bestFor: ['Severe Manglik dosha', 'Multiple marriage failures', 'Complex cases'],
      // languages: ['Sanskrit', 'Hindi', 'English'],
      specialFeatures: [
        'Master astrologer consultation',
        'Guaranteed marriage solutions',
        'Lifetime remedial support',
        'Advanced transformation techniques'
      ],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
      whatThisFeelsLike: [
        'An anushthan-style observance conducted with depth, discipline, and sustained spiritual focus, led by senior Acharyas.'
      ],
      hidden: true,
      jaapCount: '5001 Jaap',
      highlightTags: ['Kumbh Vivah', '1 Senior Acharya', 'Havan Included', 'Munga Coral maaal for the devotee'],
      siteHighlightTags: ['Kumbh Vivah ()', '1 Senior Acharya', 'Havan Included', 'Munga Coral maaal for the devotee'],
    }
  ],
  'kaal-sarp-dosh-nivaran': [
    {
      id: 'basic',
      name: 'Basic Kaal Sarp Dosh Nivaran',
      price: '₹18,000',
      originalPrice: '₹25,000',
      duration: '5 hours',
      features: [
        'Essential Kaal Sarp remedies',
        'Basic Rahu-Ketu pacification',
        'Simple serpent worship',
        'Obstacle removal rituals'
      ],
      priests: 1,
      materials: [
        'Basic serpent idols',
        'Traditional milk offerings',
        'Simple silver items',
        'Basic Nagpanchami materials',
        'Essential Rahu-Ketu items',
        'Basic remedial herbs'
      ],
      includes: [
        '3 qualified priests',
        'Basic Kaal Sarp assessment',
        'Essential serpent worship',
        'Basic Rahu-Ketu pacification',
        'Simple obstacle removal',
        'Basic remedial guidance'
      ],
      ritualSteps: [
        'Ganesh Puja',
        'Serpent deity invocation',
        'Basic Kaal Sarp remedies',
        'Milk offering ceremonies',
        'Obstacle removal prayers'
      ],
      bestFor: ['Mild Kaal Sarp dosha', 'Basic life obstacles', 'Simple remedies'],
      // languages: ['Hindi', 'Sanskrit'],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
      whatThisFeelsLike: [
        'A sincere and focused ritual performed with correctness, discipline, and devotion. Ideal for those seeking to perform the pooja properly without complexity.'
      ],
      jaapCount: '108 of Maha Mrityunjay Mantra Jaap',
      highlightTags: ['Senior Acharya', 'All Pooja Samagri included', 'Havan will be performed'],
      hidden: true,
    },
    {
      id: 'standard',
      name: 'Complete Kaal Sarp Dosh Nivaran',
      price: '₹35,000',
      originalPrice: '₹48,000',
      duration: '3-6 hours',
      features: [
        'Complete Kaal Sarp analysis',
        'Full serpent worship ceremony',
        'Advanced Rahu-Ketu pacification',
        'Comprehensive obstacle removal',
        'Life transformation rituals'
      ],
      popular: true,
      priests: 3,
      // materials: [
      //   'Premium serpent idols',
      //   'Sacred milk varieties',
      //   'Traditional silver vessels',
      //   'Complete Nagpanchami setup',
      //   'Advanced Rahu-Ketu materials',
      //   'Comprehensive remedial kit'
      // ],
      includes: [
        '2 experienced priests',
        'Travel, stay and dakshina for Kaashi Acharya(s) ji included',
        'Advanced Rahu-Ketu pacification',
        'All Puja Samagri included'
      ],
      ritualSteps: [
        'Ritual setup',
        'Kaal Sarp remedies',
        'Extended Rahu-Ketu ceremonies',
      ],
      bestFor: ['Moderate to severe Kaal Sarp dosha', 'Life obstacles', 'Career problems'],
      // languages: ['Sanskrit', 'Hindi', 'English'],
      specialFeatures: [
        'Complete serpent worship',
        'Advanced Rahu-Ketu pacification',
        'Comprehensive life transformation'
      ],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
      whatThisFeelsLike: [
        'A deeper and more immersive ritual experience with enhanced collective energy, guidance, and continuity..'
      ],
      jaapCount: 'Shiv Abhishek',
      highlightTags: ['Prayer for Rahu & Ketu', 'Silver/Copper Naag Devta Idols', '1 Senior Acharya', '108 of Maha Mrityunjay Mantra Jaap', 'Havan  included', 'All Pooja Samagri included', 'Travel(to & from Varanasi), stay, dakhsina for Kaashi Acharya(s) ji included'],
      siteHighlightTags: ['Prayer for Rahu & Ketu', 'Silver/Copper Naag Devta Idols', '1 Senior Acharya', '108 of Maha Mrityunjay Mantra Jaap', 'Havan  included', 'All Pooja Samagri included', 'Dakshina & Expense for Kaashi Acharya(s) ji included'],
    },
    {
      id: 'premium',
      name: 'Advanced Kaal Sarp Dosh Nivaran',
      price: '₹65,000',
      originalPrice: '₹85,000',
      duration: '2 days',
      features: [
        'Advanced Kaal Sarp remedies',
        'Complete life transformation',
        'Multi-day serpent worship',
        'Guaranteed obstacle removal',
        'Lifetime protection'
      ],
      priests: 7,
      materials: [
        'High-grade serpent installations',
        'Premium milk varieties',
        'Sacred silver collections',
        'Grand Nagpanchami setup',
        'Advanced remedial materials',
        'Lifetime protection kit'
      ],
      includes: [
        '7 master astrologer-priests',
        'Advanced multi-day ceremony',
        'Complete life transformation',
        'Guaranteed obstacle removal',
        'Lifetime Kaal Sarp protection',
        'Comprehensive guidance'
      ],
      ritualSteps: [
        'Advanced astrological assessment',
        'Multi-day serpent worship',
        'Complete Kaal Sarp transformation',
        'Advanced Rahu-Ketu ceremonies',
        'Guaranteed life transformation',
        'Lifetime protection rituals'
      ],
      bestFor: ['Severe Kaal Sarp dosha', 'Major life problems', 'Complete transformation'],
      // languages: ['Sanskrit', 'Hindi', 'English'],
      specialFeatures: [
        'Multi-day ceremony',
        'Complete life transformation',
        'Guaranteed obstacle removal',
        'Lifetime protection guarantee'
      ],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
      whatThisFeelsLike: [
        'An anushthan-style observance conducted with depth, discipline, and sustained spiritual focus, led by senior Acharyas.'
      ],
      hidden: true,
      jaapCount: 'Gold Sarp Included',
      highlightTags: ['Laghu Rudrabhishek', '1 Senior Acharya', 'Rahu & Ketu Shanti Havan included',  '1 Rudraksha mala for wearer'],
    }
  ],
  'pitra-dosh-nivaran': [
    {
      id: 'basic',
      name: 'Basic Pitra Dosh Nivaran',
      price: '₹12,000',
      originalPrice: '₹16,000',
      duration: '3 hours',
      features: [
        'Essential Pitra worship',
        'Basic ancestral blessings',
        'Simple Shraddh rituals',
        'Family harmony restoration'
      ],
      priests: 2,
      materials: [
        'Basic Shraddh materials',
        'Traditional food offerings',
        'Simple sacred items',
        'Basic ancestor photos',
        'Essential pinda materials',
        'Traditional incense'
      ],
      includes: [
        '2 qualified priests',
        'Basic Pitra assessment',
        'Essential ancestral worship',
        'Simple Shraddh ceremony',
        'Family harmony rituals',
        'Basic guidance'
      ],
      ritualSteps: [
        'Ganesh Puja',
        'Ancestor invocation',
        'Basic Shraddh rituals',
        'Food offering ceremonies',
        'Family blessing prayers'
      ],
      bestFor: ['Mild Pitra dosha', 'Basic family problems', 'Simple ancestral worship'],
      // languages: ['Hindi', 'Sanskrit'],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
      whatThisFeelsLike: [
        'A sincere and focused ritual performed with correctness, discipline, and devotion. Ideal for those seeking to perform the pooja properly without complexity.'
      ],
      hidden: true,
      jaapCount: 'Sankalp with name and gotra',
      highlightTags: ['Standard Pitra Dosh Nivaran', 'All Pooja Samagri included', 'Havan will be performed'],
    },
    {
      id: 'standard',
      name: 'Complete Pitra Dosh Nivaran',
      price: '₹22,000',
      originalPrice: '₹30,000',
      duration: '4-6 hours',
      features: [
        'Complete Pitra worship',
        'Full ancestral blessings',
        'Extended Shraddh ceremony',
        'Comprehensive family healing',
        'Generational blessing rituals'
      ],
      popular: true,
      priests: 2,
      // materials: [
      //   'Complete Shraddh setup',
      //   'Traditional food varieties',
      //   'Sacred ancestor materials',
      //   'Family lineage items',
      //   'Premium pinda offerings',
      //   'Extended ceremony materials'
      // ],
      includes: [
        '2 experienced priests',
        'All Puja Samagri included',
        'Kashi Acharyas expenses and dakshina included',
        '5 Bhramin Bhoj included',
        'Tarpan and pind daan will be performed at Ganga ghat',
        
      ],
      ritualSteps: [
        'Detailed family analysis',
        'Complete ancestor worship',
        'Generational blessing'
      ],
      bestFor: ['Moderate to Severe Pitra dosha', 'Family problems', 'Ancestral issues'],
      // languages: ['Sanskrit', 'Hindi', 'English'],
      // specialFeatures: [
      //   'Complete family analysis',
      //   'Extended Shraddh ceremonies',
      //   'Generational blessing rituals'
      // ],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
      whatThisFeelsLike: [
        'A deeper and more immersive ritual experience with enhanced collective energy, guidance, and continuity..'
      ],
      jaapCount: 'Pre call to understand the issue',
      highlightTags: ['Senior Acharya', 'Specific rituals based on problem', 'Pind Daan will be performed'],
      siteHighlightTags: ['Pind Daan will be performed', '1 Senior Acharya', 'All Pooja Samagri included', 'Havan will be performed', 'Option to Participate in person at our site for Sankalp & Havan', 'Online Sankalp', 'Free pickup & Drop facility from your hotel/place in Varanasi to our site'],
    },
    {
      id: 'premium',
      name: 'Advanced Pitra Dosh Nivaran',
      price: '₹40,000',
      originalPrice: '₹55,000',
      duration: '4-7 hours',
      features: [
        'Advanced Pitra remedies',
        'Complete ancestral healing',
        'Multi-generational worship',
        'Guaranteed family harmony',
        'Lifetime ancestral blessings'
      ],
      priests: 3,
      materials: [
        'Grand Shraddh installation',
        'Premium food varieties',
        'Sacred ancestral collections',
        'Multi-generational materials',
        'Advanced pinda ceremonies',
        'Lifetime blessing materials'
      ],
      includes: [
        '5 master priests',
        'Advanced family lineage analysis',
        'Complete ancestral healing',
        'Multi-generational worship',
        'Guaranteed family harmony',
        'Lifetime ancestral guidance'
      ],
      ritualSteps: [
        'Advanced lineage analysis',
        'Multi-generational worship',
        'Complete ancestral healing',
        'Advanced Shraddh ceremonies',
        'Guaranteed family transformation',
        'Lifetime blessing rituals'
      ],
      bestFor: ['Severe Pitra dosha', 'Major family problems', 'Complete ancestral healing'],
      // languages: ['Sanskrit', 'Hindi', 'English'],
      specialFeatures: [
        'Multi-generational worship',
        'Complete ancestral healing',
        'Guaranteed family harmony',
        'Lifetime ancestral blessings'
      ],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
      whatThisFeelsLike: [
        'An anushthan-style observance conducted with depth, discipline, and sustained spiritual focus, led by senior Acharyas.'
      ],
      jaapCount: 'Complete Pitra Shanti Anushthan',
      hidden: true,
      highlightTags: ['1 Senior Acharya', 'Pind Daan & Tarpan', '5 Brahmin Bhoj included'],
      siteHighlightTags: ['Pind Daan will be performed', '1 Senior Acharya', 'All Pooja Samagri included', 'Havan will be performed', 'Option to Participate in person at our site for Sankalp & Havan', 'Online Sankalp', 'Free pickup & Drop facility from your hotel/place in Varanasi to our site'],
    }
  ],
  'mool-dosh-nivaran': [
    {
      id: 'basic',
      name: 'Basic Mool Dosh Nivaran',
      price: '₹12,000',
      originalPrice: '₹16,000',
      duration: '3 hours',
      features: [
        'Essential Mool Nakshatra remedies',
        'Basic nakshatra pacification',
        'Newborn protection prayers',
        'Family safety rituals'
      ],
      priests: 2,
      includes: [
        '2 qualified priests',
        'Basic Mool Dosh assessment',
        'Essential Nakshatra shanti',
        'Basic protective mantras',
        'Family blessing ceremony',
        'Basic remedial guidance'
      ],
      ritualSteps: [
        'Ganesh Puja',
        'Nakshatra deity invocation',
        'Basic Mool Dosh remedies',
        'Protective mantra recitation',
        'Family blessing prayers'
      ],
      bestFor: ['Mild Mool Dosh', 'Basic newborn protection', 'Simple nakshatra remedies'],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
      whatThisFeelsLike: [
        'A sincere and focused ritual performed with correctness, discipline, and devotion. Ideal for those seeking to perform the pooja properly without complexity.'
      ],
      hidden: true,
      jaapCount: 'Nakshatra Shanti Jaap',
      highlightTags: ['Senior Acharya', 'All Pooja Samagri included', 'Havan will be performed'],
    },
    {
      id: 'standard',
      name: 'Complete Mool Dosh Nivaran',
      price: '₹35,000',
      originalPrice: '₹38,000',
      duration: '6-8 hours',
      features: [
        'Complete Mool Nakshatra analysis',
        'Full nakshatra pacification',
        'Comprehensive newborn protection',
        'Extended family safety rituals',
        'All dosha-specific remedies'
      ],
      popular: true,
      priests: 3,
      includes: [
        '3 experienced priests',
        'Full Mool Dosh Nivaran ceremony',
        'Kashi Acharyas travel, stay and dakshina included',
        'All Puja Samagri included',
        'Nakshatra yantra energizing',
        'Comprehensive remedial guidance'
      ],
      ritualSteps: [
        'Detailed nakshatra analysis',
        'Complete Mool Dosh remedies',
        'Havan ceremony',
        'Nakshatra shanti offerings',
        'Protection blessing rituals'
      ],
      bestFor: ['Moderate to severe Mool Dosh', 'Newborn protection', 'Family well-being'],
      specialFeatures: [
        'Birth nakshatra-specific remedy',
        'Newborn protection ceremony',
        'Complete dosha pacification'
      ],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
      whatThisFeelsLike: [
        'A deeper and more immersive ritual experience with enhanced collective energy, guidance, and continuity.'
      ],
      jaapCount: 'Mool Nakshatra Shanti Jaap',
      highlightTags: ['Pre Consultation with Senior Acharya for problem analysis', '1 Senior Acharya', 'Nakshatra-specific Havan included', '27 Different woods havan','All Pooja Samagri included', 'Travel(to & from Varanasi), stay, dakshina for Kaashi Acharya(s) ji included'],
      siteHighlightTags: ['Pre Consultation with Senior Acharya', '1 Senior Acharya', 'Nakshatra-specific Havan included', 'All Pooja Samagri included', 'Dakshina & Expense for Kaashi Acharya(s) ji included'],
      planTag: 'Most Popular',
    },
    {
      id: 'premium',
      name: 'Advanced Mool Dosh Nivaran',
      price: '₹38,000',
      originalPrice: '₹52,000',
      duration: '1-2 days',
      features: [
        'Advanced Mool Nakshatra remedies',
        'Complete birth dosha transformation',
        'Multi-ritual protection ceremony',
        'Lifelong family protection',
        'Comprehensive astrological healing'
      ],
      priests: 4,
      includes: [
        '4 master astrologer-priests',
        'Advanced nakshatra analysis',
        'Complete birth dosha transformation',
        'Multi-ritual protection ceremonies',
        'Lifelong family protection blessings',
        'Comprehensive remedial guidance'
      ],
      ritualSteps: [
        'Advanced astrological assessment',
        'Complete Mool Dosh transformation',
        'Advanced nakshatra shanti havan',
        'Multi-deity protection ceremonies',
        'Lifelong protection blessing rituals'
      ],
      bestFor: ['Severe Mool Dosh', 'Multiple family members affected', 'Complete remedial transformation'],
      specialFeatures: [
        'Master astrologer consultation',
        'Multi-ritual protection ceremony',
        'Complete birth dosha healing',
        'Lifelong family protection'
      ],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
      whatThisFeelsLike: [
        'An anushthan-style observance conducted with depth, discipline, and sustained spiritual focus, led by senior Acharyas.'
      ],
      hidden: true,
      jaapCount: 'Complete Nakshatra Shanti Anushthan',
      highlightTags: ['1 Senior Acharya', 'Multi-deity Havan included', 'Nakshatra Yantra for wearer', 'All Pooja Samagri included'],
      siteHighlightTags: ['1 Senior Acharya', 'Multi-deity Havan included', 'Nakshatra Yantra for wearer', 'All Pooja Samagri included', 'Option to Participate in person at our site for Sankalp & Havan'],
    }
  ],
  'gaya-ji-shradh-karm': [
    {
      id: 'basic',
      name: 'Basic Gaya Ji Shradh',
      price: '₹25,000',
      originalPrice: '₹35,000',
      duration: '1 day',
      features: [
        'Essential Gaya Shradh rituals',
        'Basic Pitra Paksha ceremonies',
        'Traditional pinda offering',
        'Sacred Falgu river rituals'
      ],
      priests: 2,
      materials: [
        'Basic Shradh materials',
        'Traditional pinda ingredients',
        'Sacred thread and cloth',
        'Basic food offerings',
        'Essential ceremony items',
        'River worship materials'
      ],
      includes: [
        '2 qualified Gaya priests',
        'Basic Shradh ceremony',
        'Essential pinda rituals',
        'Sacred river worship',
        'Ancestral blessing ceremony',
        'Local transportation'
      ],
      ritualSteps: [
        'Early morning Ganga Aarti',
        'Ancestral invocation at Vishnupad',
        'Traditional pinda offering',
        'Sacred Falgu river rituals',
        'Final blessing ceremony'
      ],
      bestFor: ['First-time Gaya visit', 'Essential ancestral worship', 'Budget-friendly option'],
      // languages: ['Hindi', 'Sanskrit'],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
      whatThisFeelsLike: [
        'A sincere and focused ritual performed with correctness, discipline, and devotion. Ideal for those seeking to perform the pooja properly without complexity.'
      ]
    },
    {
      id: 'standard',
      name: 'Complete Gaya Ji Shradh',
      price: '₹45,000',
      originalPrice: '₹60,000',
      duration: '2 days',
      features: [
        'Complete Gaya Shradh package',
        'Multiple sacred site visits',
        'Extended pinda ceremonies',
        'Comprehensive ancestral rituals',
        'Accommodation included'
      ],
      popular: true,
      priests: 3,
      materials: [
        'Complete Shradh setup',
        'Premium pinda materials',
        'Sacred Gaya soil',
        'Traditional food varieties',
        'Extended ceremony items',
        'Multiple site materials'
      ],
      includes: [
        '3 experienced Gaya priests',
        'Complete 2-day ceremony',
        'Multiple sacred site visits',
        'Extended pinda rituals',
        'Accommodation (1 night)',
        'All meals and transportation'
      ],
      ritualSteps: [
        'Day 1: Vishnupad temple ceremonies',
        'Complete pinda offering rituals',
        'Sacred Falgu river worship',
        'Day 2: Extended ancestral ceremonies',
        'Multiple holy site visits',
        'Final blessing and departure'
      ],
      bestFor: ['Complete Gaya experience', 'Traditional families', 'Comprehensive ancestral worship'],
      // languages: ['Sanskrit', 'Hindi', 'English'],
      specialFeatures: [
        'Multiple sacred site visits',
        'Complete accommodation package',
        'Extended ancestral ceremonies'
      ],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
      whatThisFeelsLike: [
        'A deeper and more immersive ritual experience with enhanced collective energy, guidance, and continuity..'
      ]
    },
    {
      id: 'premium',
      name: 'Grand Gaya Ji Shradh',
      price: '₹85,000',
      originalPrice: '₹1,20,000',
      duration: '3 days',
      features: [
        'Royal Gaya Shradh experience',
        'Premium accommodation',
        'Extended multi-day ceremonies',
        'VIP temple access',
        'Complete family package'
      ],
      priests: 5,
      materials: [
        'Grand Shradh installation',
        'Premium ceremonial materials',
        'Sacred Gaya collections',
        'Luxury food offerings',
        'VIP ceremony setup',
        'Complete family materials'
      ],
      includes: [
        '5 master Gaya priests',
        'Royal 3-day ceremony',
        'Premium accommodation (2 nights)',
        'VIP temple access',
        'Complete family ceremonies',
        'Luxury meal arrangements'
      ],
      ritualSteps: [
        'Day 1: Grand arrival and temple ceremonies',
        'Royal Vishnupad worship',
        'Day 2: Extended pinda ceremonies',
        'Multiple sacred site rituals',
        'Day 3: Grand final ceremonies',
        'Royal departure with blessings'
      ],
      bestFor: ['Royal family ceremonies', 'VIP treatment', 'Complete Gaya pilgrimage'],
      // languages: ['Sanskrit', 'Hindi', 'English'],
      specialFeatures: [
        'VIP temple access',
        'Premium accommodation',
        'Royal ceremony treatment',
        'Complete family package'
      ],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
      whatThisFeelsLike: [
        'An anushthan-style observance conducted with depth, discipline, and sustained spiritual focus, led by senior Acharyas.'
      ]
    }
  ],
  'kashi-shradh-karm': [
    {
      id: 'basic',
      name: 'Basic Kashi Shradh',
      price: '₹20,000',
      originalPrice: '₹28,000',
      duration: '1 day',
      features: [
        'Essential Kashi Shradh rituals',
        'Basic Ganga Aarti participation',
        'Traditional Vishwanath darshan',
        'Simple ancestral ceremonies'
      ],
      priests: 1,
      materials: [
        'Basic Shradh materials',
        'Traditional Ganga items',
        'Sacred thread and flowers',
        'Basic food offerings',
        'Essential ceremony setup',
        'Holy water collections'
      ],
      includes: [
        '1 Pandit Ji (Kashi-trained)',
        'Shraddh vidhi with pind daan & tarpan',
        'Essential Ganga rituals',
        'Essential pooja samagri',
        'Ancestral blessing ceremony',
        'Pandit Ji\'s Dakshina and local arrangements'
      ],
      ritualSteps: [
        'Sankalp',
        'Tarpan ',
        'Pind Daan',
        'Shraddh Vidhi',
        'Brahmin Bhoj and Aashirvaad'
      ],
      bestFor: ['Single ancestor Shraddh', 'First-time Shraddh in Kashi', 'Budget-friendly option'],
      // languages: ['Hindi', 'Sanskrit'],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
      whatThisFeelsLike: [
        'A sincere and scripturally correct Shraddh performed with dignity.'
      ], 
      hidden: true,
    },
    {
      id: 'standard',
      name: 'Complete Kashi Shradh',
      price: '₹38,000',
      originalPrice: '₹50,000',
      duration: '1 days',
      features: [
        'Complete Kashi Shradh package',
        'Extended Ganga ceremonies',
        'Multiple temple visits',
        'Comprehensive ancestral rituals',
        'Accommodation included'
      ],
      popular: true,
      priests: 2,
      materials: [
        'Complete Kashi setup',
        'Premium Ganga materials',
        'Sacred Kashi soil',
        'Traditional offerings',
        'Extended ceremony items',
        'Multiple temple materials'
      ],
      includes: [
        '2 Pandit Ji (Kashi-trained)',
        'Complete 2-day ceremony',
        'Extended Ganga rituals',
        'Family-level sankalp and tarpan',
        'Complete pooja samagri',
        'All pandit ji Dakshina and arrangements',
        'Pre- and post-ritual guidance and support'
      ],
      ritualSteps: [
        'Sankalp and Tarpan at Ganga',
        ' Tarpan and Pind Daan',
        'Pind Daan',
        'Pind Daan and Shraddh Vidhi',
        'Shraddh Vidhi',
        'Brahmin Bhoj and Aashirvaad'
      ],
      bestFor: ['Family Shraddh', 'Annual ancestral observance', ''],
      // languages: ['Sanskrit', 'Hindi', 'English'],
      specialFeatures: [
        'Samagri included',
        'Ritual setups ready at ghat',
      ],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
      whatThisFeelsLike: [
        'A deeper and more immersive ritual experience with enhanced collective energy, guidance, and continuity..'
      ], 
      jaapCount: 'Sankalp with name and gotra',
      highlightTags: ['Complete Kashi Shradh', 'Pind Daan & Tarpan', 'All Pooja Samagri included', 'Havan will be performed', 'Option to Participate in person at our site for Sankalp & Havan', 'Online/In person Sankalp', ],
    },
    {
      id: 'premium',
      name: 'Grand Kashi Shradh',
      price: '₹70,000',
      originalPrice: '₹95,000',
      duration: '3 days',
      features: [
        'Royal Kashi Shradh experience',
        'Premium river ceremonies',
        'VIP temple access',
        'Extended multi-day rituals',
        'Luxury accommodation'
      ],
      priests: 3,
      materials: [
        'Grand Kashi installation',
        'Premium Ganga materials',
        'Sacred collections',
        'Luxury offerings',
        'VIP ceremony setup',
        'Complete family materials'
      ],
      includes: [
        '3 Pandit Ji (Kashi-trained) led by Senior Acharya',
        'Detailed, purpose-specific sankalp and tarpan',
        'Complete Shraddh karma',
        'Full pooja samagri & arrangements',
        'All pandit ji Dakshina and arrangements',
        'Extended guidance and blessings throughout the ritual period',
      ],
      ritualSteps: [
        'Sankalp and Tarpan at Ganga',
        'Pind Daan and Shraddh Vidhi',
        'Brahmin Bhoj and Aashirvaad'
      ],
      bestFor: ['Multiple ancestors', 'Families seeking deep ancestral closure', 'Grand Shraddh ceremonies'],
      // languages: ['Sanskrit', 'Hindi', 'English'],
      specialFeatures: [
        'VIP temple access',
        'Premium river ceremonies',
        'Royal treatment',
        'Luxury accommodation'
      ],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
      whatThisFeelsLike: [
        ' A deeply immersive Shraddh conducted with continuity and guidance.'
      ],
      hidden: true,
      jaapCount: 'Complete Pitra Shanti Anushthan',
      highlightTags: ['Senior Acharya', 'Pind Daan & Tarpan', '5 Brahmin Bhoj included'],
   
    }
  ],
  'tripindi-shradh': [
    {
      id: 'basic',
      name: 'Basic Tripindi Shradh',
      price: '₹15,000',
      originalPrice: '₹22,000',
      duration: '4 hours',
      features: [
        'Essential Tripindi rituals',
        'Basic three-generation worship',
        'Simple pinda offerings',
        'Traditional ancestral ceremonies'
      ],
      priests: 3,
      materials: [
        'Basic Tripindi materials',
        'Traditional pinda ingredients',
        'Sacred items for three generations',
        'Basic food offerings',
        'Essential ceremony setup',
        'Traditional incense'
      ],
      includes: [
        '3 qualified priests',
        'Basic Tripindi ceremony',
        'Three-generation worship',
        'Essential pinda rituals',
        'Ancestral blessing ceremony',
        'Basic guidance'
      ],
      ritualSteps: [
        'Ganesh Puja',
        'Three-generation invocation',
        'Individual pinda offerings',
        'Combined ancestral worship',
        'Final blessing ceremony'
      ],
      bestFor: ['Three-generation issues', 'Basic ancestral problems', 'Simple family ceremonies'],
      // languages: ['Hindi', 'Sanskrit'],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
      whatThisFeelsLike: [
        'A sincere and focused ritual performed with correctness, discipline, and devotion. Ideal for those seeking to perform the pooja properly without complexity.'
      ],
      hidden: true,
    },
    {
      id: 'standard',
      name: 'Complete Tripindi Shradh',
      price: '₹28,000',
      originalPrice: '₹38,000',
      duration: '6 hours',
      features: [
        'Complete Tripindi ceremony',
        'Extended three-generation rituals',
        'Comprehensive pinda offerings',
        'Advanced ancestral healing',
        'Family harmony restoration'
      ],
      popular: true,
      priests: 2,
      materials: [
        'Complete Tripindi setup',
        'Premium pinda materials',
        'Sacred three-generation items',
        'Traditional food varieties',
        'Extended ceremony materials',
        'Family harmony items'
      ],
      includes: [
        '4 experienced priests',
        'Complete Tripindi ceremony',
        'Extended generation worship',
        'Comprehensive pinda rituals',
        'Advanced ancestral healing',
        'Family harmony guidance'
      ],
      ritualSteps: [
        'Elaborate ancestral invocation',
        'Extended three-generation worship',
        'Comprehensive pinda ceremonies',
        'Advanced healing rituals',
        'Family harmony restoration',
        'Complete blessing ceremony'
      ],
      bestFor: ['Complex ancestral issues', 'Family harmony problems', 'Complete three-generation healing'],
      // languages: ['Sanskrit', 'Hindi', 'English'],
      specialFeatures: [
        'Extended generation worship',
        'Advanced ancestral healing',
        'Family harmony restoration'
      ],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
      whatThisFeelsLike: [
        'A deeper and more immersive ritual experience with enhanced collective energy, guidance, and continuity..'
      ],
       planTag: 'Family Choice',
       jaapCount: 'Complete 3 Generation Pitra Shanti Anushthan',
       highlightTags: ['Senior Acharya', 'Pre call to understand the issue', 'All Pooja Samagri Included', 'Pind Daan & Tarpan', '5 Brahmin Bhoj included', 'Travel assistance for family members'],
   
    },
    {
      id: 'premium',
      name: 'Advanced Tripindi Shradh',
      price: '₹50,000',
      originalPrice: '₹70,000',
      duration: '2 days',
      features: [
        'Advanced Tripindi ceremony',
        'Multi-day generation worship',
        'Complete ancestral transformation',
        'Guaranteed family harmony',
        'Lifetime ancestral blessings'
      ],
      priests: 6,
      materials: [
        'Grand Tripindi installation',
        'Premium ceremonial materials',
        'Sacred generation collections',
        'Luxury food offerings',
        'Advanced healing materials',
        'Lifetime blessing items'
      ],
      includes: [
        '6 master priests',
        'Advanced 2-day ceremony',
        'Complete generation transformation',
        'Guaranteed family harmony',
        'Lifetime ancestral guidance',
        'Extended family counseling'
      ],
      ritualSteps: [
        'Day 1: Grand ancestral invocation',
        'Extended generation ceremonies',
        'Advanced healing rituals',
        'Day 2: Complete transformation',
        'Guaranteed harmony restoration',
        'Lifetime blessing ceremonies'
      ],
      bestFor: ['Severe ancestral issues', 'Major family problems', 'Complete transformation'],
      // languages: ['Sanskrit', 'Hindi', 'English'],
      specialFeatures: [
        'Multi-day ceremony',
        'Complete ancestral transformation',
        'Guaranteed family harmony',
        'Lifetime guidance'
      ],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
      whatThisFeelsLike: [
        'An anushthan-style observance conducted with depth, discipline, and sustained spiritual focus, led by senior Acharyas.'
      ],
      hidden: true,
    }
  ],
  'narayan-bali': [
    {
      id: 'basic',
      name: 'Basic Narayan Bali',
      price: '₹30,000',
      originalPrice: '₹42,000',
      duration: '6 hours',
      features: [
        'Essential Narayan Bali rituals',
        'Basic Kaal Sarp remedies',
        'Simple Vishnu worship',
        'Traditional bali ceremonies'
      ],
      priests: 1,
      materials: [
        'Basic Narayan Bali materials',
        'Traditional bali ingredients',
        'Sacred Vishnu items',
        'Basic remedy materials',
        'Essential ceremony setup',
        'Traditional offerings'
      ],
      includes: [
        '1–2 Kashi-trained Pandit Ji',
        'Narayan Bali ceremony',
        'Sankalp for the departed sou',
        'Essential pooja samagri',
        'Pind daan & symbolic offerings',
        'Pandit Ji dakshina'
      ],
      ritualSteps: [
        'Sankalp',
        'Narayan Bali Vidhi',
        'Pind Daan',
        'Tarpan',
        'Shanti Paath'
      ],
      bestFor: ['Single ancestral observance', 'Completion of pending rites', 'Essential remedies'],
      // languages: ['Hindi', 'Sanskrit'],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
      whatThisFeelsLike: [
        'A focused and respectful Narayan Bali ritual.'
      ]
    },
    {
      id: 'standard',
      name: 'Complete Narayan Bali',
      price: '₹55,000',
      originalPrice: '₹75,000',
      duration: '8 hours',
      features: [
        'Complete Narayan Bali ceremony',
        'Full Kaal Sarp remedies',
        'Extended Vishnu worship',
        'Comprehensive bali rituals',
        'Advanced problem resolution'
      ],
      popular: true,
      priests: 2,
      materials: [
        'Complete Narayan Bali setup',
        'Premium bali materials',
        'Sacred Vishnu collections',
        'Advanced remedy items',
        'Extended ceremony materials',
        'Complete offering setup'
      ],
      includes: [
        '2–3 Pandit Ji (Kashi-trained) led by Senior Acharya',
        'Complete Narayan Bali ceremony',
        'Pre- and post-ritual guidance',
        'Family-level sankalp',
        'Complete pooja samagri',
        'Pandit Ji dakshina'
      ],
      ritualSteps: [
        'Detailed Sankalp',
        'Narayan Bali Vidhi',
        'Pind Daan',
        'Tarpan',
        'Shanti Paath'
      ],
      bestFor: ['Families seeking deeper reassurance', 'Multi-generation intent', 'Comprehensive remedies'],
      // languages: ['Sanskrit', 'Hindi', 'English'],
      specialFeatures: [
        'Complete bali ceremonies',
        'Extended Vishnu worship',
        'Advanced problem resolution'
      ],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
      whatThisFeelsLike: [
        'A more structured and complete Narayan Bali observance with enhanced guidance and reassurance.'
      ]
    },
    {
      id: 'premium',
      name: 'Grand Narayan Bali',
      price: '₹95,000',
      originalPrice: '₹1,30,000',
      duration: '1 day',
      features: [
        'Grand Narayan Bali ceremony',
        'Complete Kaal Sarp transformation',
        'Multi-day Vishnu worship',
        'Guaranteed problem resolution',
        'Lifetime protection'
      ],
      priests: 3-4,
      materials: [
        'Grand Narayan Bali installation',
        'Premium ceremonial materials',
        'Sacred Vishnu collections',
        'Advanced remedy setup',
        'Luxury ceremony materials',
        'Lifetime protection items'
      ],
      includes: [
        'Senior Acharya with 3–4 Pandit Ji (Kashi-trained)',
        'Purpose-specific sankalp for the departed soul and family',
        'Complete Narayan Bali ritual',
        'Full pooja samagri & arrangements',
        'Pandit Ji dakshina',
        'Extended guidance and blessings'
      ],
      ritualSteps: [
        'Detailed Sankalp',
        'Extended Narayan Bali Vidhi',
        'Pind Daan',
        'Tarpan',
        'Shanti Paath'
      ],
      bestFor: ['Long-standing ancestral matters', 'Families seeking complete ritual closure'],
      // languages: ['Sanskrit', 'Hindi', 'English'],
      specialFeatures: [
        'Multi-day ceremony',
        'Complete Kaal Sarp transformation',
        'Guaranteed problem resolution',
        'Lifetime protection'
      ],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
      whatThisFeelsLike: [
        'A deeply guided Narayan Bali conducted with discipline and continuity.'
      ]
    }
  ],
  'akhand-ramcharitmanas': [
    {
      id: 'basic',
      name: 'Basic Akhand Ramcharitmanas',
      price: '₹15,000',
      originalPrice: '₹22,000',
      duration: '24 hours',
      features: [
        'Continuous 24-hour recitation',
        'Basic setup with devotional atmosphere',
        'Traditional Rama worship',
        'Community participation'
      ],
      priests: 4,
      materials: [
        'Sacred Ramcharitmanas copies',
        'Traditional lamps and decorations',
        'Basic flowers and garlands',
        'Simple prasadam ingredients',
        'Essential puja items',
        'Continuous incense'
      ],
      includes: [
        '4 qualified reciters (rotating shifts)',
        '24-hour continuous recitation',
        'Basic ceremonial setup',
        'Traditional Rama worship',
        'Community prasadam',
        'Basic guidance and coordination'
      ],
      ritualSteps: [
        'Opening Ganesh Puja',
        'Rama deity installation',
        'Continuous recitation begins',
        'Periodic aarti ceremonies',
        'Final completion ceremony'
      ],
      bestFor: ['Community devotion', 'Spiritual merit', 'Basic continuous recitation'],
      // languages: ['Hindi', 'Sanskrit'],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
      whatThisFeelsLike: [
        'A sincere and focused ritual performed with correctness, discipline, and devotion. Ideal for those seeking to perform the pooja properly without complexity.'
      ]
    },
    {
      id: 'standard',
      name: 'Traditional Akhand Ramcharitmanas',
      price: '₹28,000',
      originalPrice: '₹38,000',
      duration: '24 hours',
      features: [
        'Complete 24-hour sacred recitation',
        'Traditional decorative setup',
        'Extended Rama worship ceremonies',
        'Enhanced community participation',
        'Professional coordination'
      ],
      popular: true,
      priests: 6,
      materials: [
        'Premium Ramcharitmanas editions',
        'Beautiful traditional decorations',
        'Fresh flower arrangements',
        'Premium prasadam varieties',
        'Complete puja setup',
        'Continuous lamp arrangements'
      ],
      includes: [
        '6 experienced reciters',
        'Complete 24-hour recitation',
        'Traditional ceremonial setup',
        'Extended Rama worship',
        'Enhanced community prasadam',
        'Professional coordination'
      ],
      ritualSteps: [
        'Grand opening ceremonies',
        'Beautiful Rama installation',
        'Continuous melodious recitation',
        'Regular aarti and worship',
        'Grand completion celebration'
      ],
      bestFor: ['Traditional families', 'Community celebrations', 'Complete devotional experience'],
      // languages: ['Sanskrit', 'Hindi', 'English'],
      specialFeatures: [
        'Melodious recitation',
        'Traditional decorative setup',
        'Enhanced community experience'
      ],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
      whatThisFeelsLike: [
        'A deeper and more immersive ritual experience with enhanced collective energy, guidance, and continuity..'
      ]
    },
    {
      id: 'premium',
      name: 'Grand Akhand Ramcharitmanas',
      price: '₹50,000',
      originalPrice: '₹70,000',
      duration: '24 hours',
      features: [
        'Royal 24-hour recitation ceremony',
        'Luxury decorative arrangements',
        'Multiple reciter teams',
        'Grand community celebration',
        'Professional event management'
      ],
      priests: 10,
      materials: [
        'Luxury Ramcharitmanas collections',
        'Grand traditional decorations',
        'Exotic flower arrangements',
        'Premium feast preparations',
        'Grand ceremonial setup',
        'Continuous lighting arrangements'
      ],
      includes: [
        '10 master reciters (multiple teams)',
        'Royal 24-hour ceremony',
        'Luxury ceremonial setup',
        'Grand Rama worship',
        'Community feast arrangements',
        'Complete event management'
      ],
      ritualSteps: [
        'Royal opening ceremonies',
        'Grand Rama installation',
        'Multiple team recitation',
        'Grand aarti celebrations',
        'Royal completion feast'
      ],
      bestFor: ['Grand community events', 'Luxury celebrations', 'Large-scale devotional gatherings'],
      // languages: ['Sanskrit', 'Hindi', 'English'],
      specialFeatures: [
        'Multiple reciter teams',
        'Royal ceremony setup',
        'Grand community feast',
        'Complete event management'
      ],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
      whatThisFeelsLike: [
        'An anushthan-style observance conducted with depth, discipline, and sustained spiritual focus, led by senior Acharyas.'
      ]
    }
  ],
  'sunder-kand-path': [
    {
      id: 'basic',
      name: 'Basic Sunder Kand Path',
      price: '₹3,500',
      originalPrice: '₹5,000',
      duration: '2 hours',
      features: [
        'Complete Sunder Kand recitation',
        'Basic Hanuman worship',
        'Traditional setup',
        'Simple prasadam'
      ],
      priests: 1,
      materials: [
        'Sacred Sunder Kand book',
        'Basic Hanuman decorations',
        'Simple flowers',
        'Traditional lamps',
        'Basic prasadam ingredients',
        'Essential puja items'
      ],
      includes: [
        '1 qualified reciter',
        'Complete Sunder Kand recitation',
        'Basic Hanuman worship',
        'Traditional ceremony setup',
        'Simple prasadam distribution',
        'Basic spiritual guidance'
      ],
      ritualSteps: [
        'Opening Hanuman prayer',
        'Complete Sunder Kand recitation',
        'Hanuman Chalisa chanting',
        'Aarti ceremony',
        'Prasadam distribution'
      ],
      bestFor: ['Weekly devotion', 'Personal spiritual practice', 'Simple family ceremonies'],
      // languages: ['Hindi'],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
      whatThisFeelsLike: [
        'A sincere and focused ritual performed with correctness, discipline, and devotion. Ideal for those seeking to perform the pooja properly without complexity.'
      ]
    },
    {
      id: 'standard',
      name: 'Traditional Sunder Kand Path',
      price: '₹6,500',
      originalPrice: '₹9,000',
      duration: '3 hours',
      features: [
        'Complete melodious recitation',
        'Extended Hanuman worship',
        'Traditional decorative setup',
        'Enhanced prasadam varieties',
        'Community participation'
      ],
      popular: true,
      priests: 2,
      materials: [
        'Premium Sunder Kand edition',
        'Beautiful Hanuman decorations',
        'Fresh flower garlands',
        'Traditional lamp arrangements',
        'Premium prasadam ingredients',
        'Extended puja setup'
      ],
      includes: [
        '2 experienced reciters',
        'Melodious Sunder Kand recitation',
        'Extended Hanuman worship',
        'Traditional ceremonial setup',
        'Enhanced prasadam varieties',
        'Community participation guidance'
      ],
      ritualSteps: [
        'Elaborate Hanuman invocation',
        'Melodious Sunder Kand recitation',
        'Extended Hanuman Chalisa',
        'Traditional aarti ceremony',
        'Enhanced prasadam celebration'
      ],
      bestFor: ['Traditional families', 'Community devotion', 'Enhanced spiritual experience'],
      // languages: ['Hindi', 'Sanskrit'],
      specialFeatures: [
        'Melodious recitation',
        'Traditional decorative setup',
        'Enhanced community experience'
      ],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
      whatThisFeelsLike: [
        'A deeper and more immersive ritual experience with enhanced collective energy, guidance, and continuity..'
      ]
    },
    {
      id: 'premium',
      name: 'Grand Sunder Kand Path',
      price: '₹12,000',
      originalPrice: '₹18,000',
      duration: '4 hours',
      features: [
        'Royal Sunder Kand ceremony',
        'Grand Hanuman worship',
        'Luxury decorative arrangements',
        'Premium prasadam feast',
        'Professional coordination'
      ],
      priests: 3,
      materials: [
        'Luxury Sunder Kand collection',
        'Grand Hanuman decorations',
        'Exotic flower arrangements',
        'Premium lamp setups',
        'Luxury prasadam preparations',
        'Grand ceremonial materials'
      ],
      includes: [
        '3 master reciters',
        'Royal Sunder Kand ceremony',
        'Grand Hanuman worship',
        'Luxury ceremonial setup',
        'Premium prasadam feast',
        'Professional coordination'
      ],
      ritualSteps: [
        'Grand Hanuman installation',
        'Royal Sunder Kand recitation',
        'Extended worship ceremonies',
        'Grand aarti celebration',
        'Premium prasadam feast'
      ],
      bestFor: ['Grand celebrations', 'Luxury devotional experience', 'Special occasions'],
      // languages: ['Sanskrit', 'Hindi', 'English'],
      specialFeatures: [
        'Royal ceremony setup',
        'Grand Hanuman worship',
        'Premium prasadam feast',
        'Professional coordination'
      ],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
      whatThisFeelsLike: [
        'An anushthan-style observance conducted with depth, discipline, and sustained spiritual focus, led by senior Acharyas.'
      ]
    }
  ],
  'hanuman-chalisa-path': [
    {
      id: 'basic',
      name: 'Basic Hanuman Chalisa Path',
      price: '₹2,000',
      originalPrice: '₹3,000',
      duration: '1 hour',
      features: [
        'Complete Chalisa recitation',
        'Basic Hanuman worship',
        'Simple devotional setup',
        'Traditional prasadam'
      ],
      priests: 1,
      materials: [
        'Sacred Chalisa book',
        'Simple Hanuman decorations',
        'Basic flowers',
        'Traditional lamp',
        'Simple prasadam items',
        'Essential puja materials'
      ],
      includes: [
        'Team of singers with 1 Pandit Ji',
        '11 Hanuman Chalisa Paath (musical)',
        'Standard sankalp and aarti',
        'Essential pooja samagri',
        'Pandit Ji (and team) dakshina and travel charges'
      ],
      ritualSteps: [
        'Ganesh Poojan',
        'Sankalp',
        '11 Hanuman Chalisa Paath (musical)',
        'Aarti and blessings',
        'Prasadam distribution'
      ],
      bestFor: ['Personal strength and discipline', 'Regular devotional practice', 'Quick spiritual ceremonies'],
      // languages: ['Hindi'],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
      whatThisFeelsLike: [
        'A concise yet energising devotional practice.'
      ]
    },
    {
      id: 'standard',
      name: 'Traditional Hanuman Chalisa Path',
      price: '₹4,000',
      originalPrice: '₹6,000',
      duration: '1.5 hours',
      features: [
        'Multiple Chalisa recitations',
        'Extended Hanuman worship',
        'Traditional setup with decorations',
        'Enhanced prasadam varieties',
        'Community participation'
      ],
      popular: true,
      priests: 2,
      materials: [
        'Premium Chalisa editions',
        'Beautiful Hanuman setup',
        'Fresh flower decorations',
        'Multiple lamp arrangements',
        'Premium prasadam ingredients',
        'Extended puja materials'
      ],
      includes: [
        'Team of singers with 1 Pandit Ji',
        '21 Hanuman Chalisa Paath (musical)',
        'Expanded sankalp',
        'Complete pooja setup and samagri',
        'Pandit Ji (and team) dakshina and travel charges',
        'Blessing ceremony'
      ],
      ritualSteps: [
        'Ganesh Poojan',
        'Detailed Sankalp',
        '21 Hanuman Chalisa Paath',
        'Aarti and blessings'
      ],
      bestFor: ['Family devotion', 'Building emotional resilience', 'Community participation'],
      // languages: ['Hindi', 'Sanskrit'],
      specialFeatures: [
        'Multiple recitations',
        'Traditional decorative setup',
        'Community participation'
      ],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
      whatThisFeelsLike: [
        'A stronger collective chanting with deeper resonance.'
      ]
    },
    {
      id: 'premium',
      name: 'Grand Hanuman Chalisa Path',
      price: '₹8,000',
      originalPrice: '₹12,000',
      duration: '2 hours',
      features: [
        'Royal Chalisa ceremony',
        'Extended multiple recitations',
        'Grand Hanuman worship',
        'Luxury decorative setup',
        'Premium celebration'
      ],
      priests: 3,
      materials: [
        'Luxury Chalisa collection',
        'Grand Hanuman decorations',
        'Premium flower arrangements',
        'Multiple ceremonial lamps',
        'Luxury prasadam preparations',
        'Grand celebration materials'
      ],
      includes: [
        'Larger team of singers led by Senior Pandit Ji',
        '108 Hanuman Chalisa Paath (musical)',
        'Purpose-specific sankalp',
        'Complete ritual setup and samagri',
        'Pandit Ji (and team) dakshina and travel charges',
        'Pre- and post-paath guidance'
      ],
      ritualSteps: [
        'Detailed Sankalp',
        'Detailed Sankalp',
        'Aarti',
        'Shanti Paath and blessings'
      ],
      bestFor: ['Periods requiring courage and steadiness', 'Deep Hanuman bhakti and discipline', 'Grand devotional celebrations'],
      // languages: ['Sanskrit', 'Hindi', 'English'],
      specialFeatures: [
        'Extended multiple recitations',
        'Grand ceremonial setup',
        'Premium celebration',
        'Luxury devotional experience'
      ],
      minAdvanceBooking: '10 days',
      cancellationPolicy: 'Canecellation: As per the cancellation policy.',
      whatThisFeelsLike: [
        'An intense and disciplined Hanuman upasana.'
      ]
    }
  ]
};